import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Login } from '@/views/Login';
import { apiFetch, getDisplayName } from '@/lib/auth';
import { type RecordingPolicy, type RecordingTopic, DEFAULT_POLICY, normalizePolicy } from '@/lib/recordingPolicy';
import { fileToLogoDataUrl, fileToBackgroundDataUrl } from '@/lib/imageResize';
import bamoaLogo from '@/assets/bamoa-logo.jpg';

/**
 * Welle 9 — Admin-App (Mandanten-Administration).
 *
 * Eigener Top-Level-Branch via ?admin (analog ?gast/?companion), erreichbar unter
 * agent.vs.bamoa.de/?admin. Nutzt den bestehenden Login wieder; nur Rolle ADMIN/
 * SUPERADMIN bekommt die Verwaltung zu sehen. Schreibende Policy-Änderungen sind
 * zusätzlich serverseitig durch assertAdmin abgesichert (UI-Gate ist nur Komfort).
 *
 * Increment 1: Sicherheit & Verschlüsselung (E2EE-/Auth-Policy) + Übersicht
 * (Berater, Gruppen). Audit-Log, Stelen, Link-Verwaltung folgen.
 */
const ADMIN_TABS = [
  { id: 'overview', label: 'Übersicht' },
  { id: 'stats', label: 'Statistik' },
  { id: 'users', label: 'Benutzer & Gruppen' },
  { id: 'signing', label: 'Signatur & Identität' },
  { id: 'comms', label: 'Telefonie & Chat' },
  { id: 'devices', label: 'Stelen' },
  { id: 'security', label: 'Sicherheit & Verschlüsselung' },
  { id: 'branding', label: 'Darstellung' },
  { id: 'audit', label: 'Audit' },
] as const;
type AdminTabId = (typeof ADMIN_TABS)[number]['id'];

export default function AdminApp() {
  const [adminTab, setAdminTab] = useState<AdminTabId>('overview');
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('bamoa_token'));
  const [role, setRole] = useState<string | null>(() => localStorage.getItem('bamoa_user_role'));

  useEffect(() => {
    const onLogin = () => {
      setToken(localStorage.getItem('bamoa_token'));
      setRole(localStorage.getItem('bamoa_user_role'));
    };
    window.addEventListener('bamoa:logged-in', onLogin);
    // pre234: Auto-Logout (401 → clearToken) muss die Admin-UI zurück zur Login-Maske
    // bringen — sonst bleibt sie mit totem Token stehen und ALLE Aktionen melden
    // "Unauthorized", während localStorage längst leer ist.
    const onLogout = () => { setToken(null); setRole(null); };
    window.addEventListener('bamoa:logout', onLogout);
    return () => { window.removeEventListener('bamoa:logged-in', onLogin); window.removeEventListener('bamoa:logout', onLogout); };
  }, []);

  // Bereits vor diesem Release eingeloggte Nutzer haben evtl. keine gespeicherte Rolle
  // (die JWT trägt sie nicht). Defensiv aus /api/me nachladen, falls dort vorhanden.
  useEffect(() => {
    if (!token || role) return;
    let alive = true;
    apiFetch('/api/me')
      .then((r) => (r.ok ? r.json() : null))
      .then((d: any) => {
        const r = d?.role || d?.membership?.role || d?.user?.role || null;
        if (alive && r) { localStorage.setItem('bamoa_user_role', r); setRole(r); }
      })
      .catch(() => { /* /api/me ggf. ohne role — dann greift Re-Login */ });
    return () => { alive = false; };
  }, [token, role]);

  const logout = () => {
    ['bamoa_token', 'bamoa_user_id', 'bamoa_user_name', 'bamoa_tenant_id', 'bamoa_user_role'].forEach((k) => localStorage.removeItem(k));
    window.location.reload();
  };

  if (!token) return <Login />;

  const isAdmin = role === 'ADMIN' || role === 'SUPERADMIN' || role === 'TENANT_ADMIN';
  const isAuditor = role === 'AUDITOR';
  const hasAccess = isAdmin || isAuditor;

  return (
    <div style={shell}>
      <style>{ADMIN_CSS}</style>
      <header style={headerBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src={bamoaLogo} alt="Bamoa" style={{ height: 30, borderRadius: 6 }} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '.01em' }}>{isAuditor && !isAdmin ? 'Audit' : 'Administration'}</div>
            <div style={{ fontSize: 11.5, color: 'var(--muted, #8aa)' }}>Bamoa Videoservice</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 13, color: 'var(--muted, #8aa)' }}>{getDisplayName() || ''}{role ? ` · ${role}` : ''}</span>
          <button onClick={logout} style={logoutBtn}>Abmelden</button>
        </div>
      </header>

      {!hasAccess ? (
        <div style={deniedWrap}>
          <div style={{ fontSize: 40, marginBottom: 6 }}>🔒</div>
          <h1 style={{ fontSize: 22, margin: 0 }}>Kein Zugriff</h1>
          <p style={{ color: 'var(--muted, #8aa)', maxWidth: 380, textAlign: 'center', lineHeight: 1.5 }}>
            Dieser Bereich ist Mandanten-Administratoren und Auditoren vorbehalten. Falls du Zugriff haben solltest,
            melde dich bitte neu an – ältere Sitzungen kennen die Rolle noch nicht.
          </p>
          <a href="/" style={{ ...logoutBtn, textDecoration: 'none', display: 'inline-block' }}>Zur Berater-App</a>
        </div>
      ) : isAuditor && !isAdmin ? (
        // Reiner Auditor: ausschließlich Audit-Log, schreibgeschützt.
        <main className="admin-content" style={contentWrap}>
          <StatsPanel />
          <AuditPanel readOnly />
        </main>
      ) : (
        <main className="admin-content" style={contentWrap}>
          {/* pre244: Tab-Navigation — die Admin-Ansicht war als Endlos-Liste unübersichtlich.
              Panels sind unverändert, nur thematisch gruppiert. */}
          <nav style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18, position: 'sticky', top: 0, zIndex: 5, background: 'var(--bg, #0d1117)', padding: '10px 0', borderBottom: '1px solid var(--hairline, #232323)' }}>
            {ADMIN_TABS.map((t) => (
              <button key={t.id} onClick={() => setAdminTab(t.id)}
                style={{
                  padding: '8px 14px', borderRadius: 999, fontSize: 13, cursor: 'pointer',
                  border: adminTab === t.id ? '1px solid var(--accent, #e2001a)' : '1px solid var(--hairline, #333)',
                  background: adminTab === t.id ? 'var(--accent, #e2001a)' : 'transparent',
                  color: adminTab === t.id ? '#fff' : 'inherit', fontWeight: adminTab === t.id ? 700 : 500,
                }}>
                {t.label}
              </button>
            ))}
          </nav>
          {adminTab === 'overview' && <OverviewPanel />}
          {adminTab === 'stats' && <StatsPanel />}
          {adminTab === 'security' && <SecurityPanel />}
          {adminTab === 'users' && (<>
            <UserManagementPanel />
            <GroupsPanel />
          </>)}
          {adminTab === 'signing' && (<>
            <SealCertPanel />
            <SmsOtpPanel />
            <SignFlowPanel />
          </>)}
          {adminTab === 'comms' && (<>
            <TelephonyPanel />
            <ConnectorAdminPanel />
            <ChatPolicyPanel />
          </>)}
          {adminTab === 'devices' && <StelesPanel />}
          {adminTab === 'branding' && <BrandingPanel />}
          {adminTab === 'audit' && <AuditPanel />}
        </main>
      )}
    </div>
  );
}

// ── Sicherheit & Verschlüsselung ────────────────────────────────────────────
function SecurityPanel() {
  const [e2ee, setE2ee] = useState<string | null>(null);
  const [authPolicy, setAuthPolicy] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        apiFetch('/api/tenant-admin/e2ee-policy').then((r) => (r.ok ? r.json() : null)),
        apiFetch('/api/tenant-admin/auth-policy').then((r) => (r.ok ? r.json() : null)),
      ]);
      if (a) setE2ee(a.e2eePolicy);
      if (b) setAuthPolicy(b.policy);
      if (!a || !b) setMsg('Aktuelle Einstellung konnte nicht geladen werden.');
    } catch { setMsg('Aktuelle Einstellung konnte nicht geladen werden.'); }
    finally { setLoaded(true); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const saveE2ee = async (policy: string) => {
    setBusy('e2ee'); setMsg('');
    try {
      const r = await apiFetch('/api/tenant-admin/e2ee-policy', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ policy }),
      });
      if (r.ok) { setE2ee(policy); setMsg('Gespeichert.'); }
      else if (r.status === 403) setMsg('Keine Berechtigung für diese Änderung.');
      else setMsg('Speichern fehlgeschlagen.');
    } catch { setMsg('Verbindung fehlgeschlagen.'); }
    finally { setBusy(null); setTimeout(() => setMsg(''), 2500); }
  };

  const saveAuth = async (policy: string) => {
    setBusy('auth'); setMsg('');
    try {
      const r = await apiFetch('/api/tenant-admin/auth-policy', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ policy }),
      });
      if (r.ok) { setAuthPolicy(policy); setMsg('Gespeichert.'); }
      else if (r.status === 403) setMsg('Keine Berechtigung für diese Änderung.');
      else setMsg('Speichern fehlgeschlagen.');
    } catch { setMsg('Verbindung fehlgeschlagen.'); }
    finally { setBusy(null); setTimeout(() => setMsg(''), 2500); }
  };

  return (
    <section style={card}>
      <div style={cardHead}>Sicherheit & Verschlüsselung</div>

      <div style={{ marginBottom: 22 }}>
        <div style={settingTitle}>Ende-zu-Ende-Verschlüsselung (SFrame)</div>
        <div style={settingHint}>
          Bei <strong>An</strong> werden Stele↔Berater-Gespräche zusätzlich Ende-zu-Ende verschlüsselt (Schlüssel
          nie auf dem Server). Bei <strong>Aus</strong> bleibt es bei der Transportverschlüsselung (DTLS-SRTP).
          Bei zugeschalteten Experten pausiert SFrame ohnehin automatisch.
        </div>
        <div style={segRow}>
          <button style={seg(e2ee === 'on')} disabled={busy === 'e2ee' || !loaded} onClick={() => saveE2ee('on')}>An · Ende-zu-Ende</button>
          <button style={seg(e2ee === 'off')} disabled={busy === 'e2ee' || !loaded} onClick={() => saveE2ee('off')}>Aus · nur Transport</button>
        </div>
      </div>

      <div>
        <div style={settingTitle}>Zwei-Faktor-Authentifizierung (Berater-Login)</div>
        <div style={settingHint}>
          Steuert, ob Berater beim Login einen zweiten Faktor (Authenticator-App) benötigen.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {[
            { v: 'password-only', label: 'Nur Passwort', hint: 'Keine 2FA.' },
            { v: 'totp-optional', label: '2FA optional', hint: 'Berater können 2FA aktivieren, müssen aber nicht.' },
            { v: 'totp-required', label: '2FA verpflichtend', hint: 'Alle Berater müssen 2FA einrichten.' },
          ].map((o) => (
            <button key={o.v} onClick={() => saveAuth(o.v)} disabled={busy === 'auth' || !loaded} style={radioRow(authPolicy === o.v)}>
              <span style={radioDot(authPolicy === o.v)} />
              <span style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>{o.label}</span>
                <span style={{ fontSize: 12, color: 'var(--muted, #8aa)' }}>{o.hint}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {msg && <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--tenant-accent, #7c3aed)' }}>{msg}</div>}
    </section>
  );
}

// ── Telefonie-Kopplung ──────────────────────────────────────────────────────
interface TpConfig {
  provider: string; enabled: boolean; clientId: string; redirectUri: string; hasSecret: boolean;
  msTenantId: string; publicBaseUrl: string; hasWebhookSecret: boolean;
  pollSeconds?: number | null;
}

function TpField({ label, value, onChange, type, placeholder }: { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, color: 'var(--muted, #8aa)' }}>{label}</span>
      <input
        type={type || 'text'} value={value} placeholder={placeholder || ''}
        autoComplete="off" spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
        style={{ padding: '7px 9px', borderRadius: 7, border: '1px solid var(--hairline, #444)', background: 'var(--bg-elev-2)', color: 'inherit', fontSize: 12.5, width: '100%', boxSizing: 'border-box' }}
      />
    </label>
  );
}

function TpProviderForm({ initial, onSaved }: { initial: TpConfig; onSaved: (m: string) => void }) {
  const isGraph = initial.provider === 'graph';
  const label = isGraph ? 'Microsoft Teams (Graph)' : 'Cisco Webex';
  const [clientId, setClientId] = useState(initial.clientId);
  const [clientSecret, setClientSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState(initial.redirectUri);
  const [enabled, setEnabled] = useState(initial.enabled);
  const [msTenantId, setMsTenantId] = useState(initial.msTenantId);
  const [publicBaseUrl, setPublicBaseUrl] = useState(initial.publicBaseUrl);
  const [webhookSecret, setWebhookSecret] = useState('');
  const [pollSeconds, setPollSeconds] = useState(
    initial.pollSeconds && initial.pollSeconds > 0 ? String(initial.pollSeconds) : '',
  );
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body: Record<string, unknown> = { provider: initial.provider, clientId, redirectUri, enabled };
      if (clientSecret.trim()) body.clientSecret = clientSecret.trim();
      if (isGraph) {
        body.msTenantId = msTenantId;
        body.publicBaseUrl = publicBaseUrl;
        if (webhookSecret.trim()) body.webhookSecret = webhookSecret.trim();
      } else {
        body.pollSeconds = pollSeconds.trim() ? Number(pollSeconds) : 0; // 0 ⇒ Default
      }
      const r = await apiFetch('/api/tenant-admin/telephony-config', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (r.ok) { setClientSecret(''); setWebhookSecret(''); onSaved(`${label}: gespeichert.`); }
      else if (r.status === 403) onSaved('Keine Berechtigung für diese Änderung.');
      else onSaved('Speichern fehlgeschlagen.');
    } catch { onSaved('Verbindung fehlgeschlagen.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ border: '1px solid var(--hairline, #232323)', borderRadius: 10, padding: 14, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600 }}>{label}</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> aktiv
        </label>
      </div>
      <details style={{ marginBottom: 10 }}>
        <summary style={{ fontSize: 12, color: 'var(--tenant-accent, #7c3aed)', cursor: 'pointer', userSelect: 'none' }}>
          Woher bekomme ich diese Werte?
        </summary>
        {isGraph ? (
          <ol style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--muted, #8aa)', lineHeight: 1.55 }}>
            <li><a href="https://entra.microsoft.com" target="_blank" rel="noreferrer" style={{ color: 'var(--tenant-accent, #7c3aed)' }}>Microsoft Entra Admin Center</a> → App-Registrierungen → neue Registrierung (oder vorhandene App).</li>
            <li>Übersichtsseite: <strong>Anwendungs-(Client-)ID</strong> und <strong>Verzeichnis-(Mandanten-)ID</strong> kopieren.</li>
            <li><strong>Zertifikate &amp; Geheimnisse</strong> → <em>Neuer geheimer Clientschlüssel</em> → den <strong>Wert</strong> kopieren (nur einmal sichtbar) = Client Secret.</li>
            <li>Als <strong>Redirect-URI</strong> (Typ Web) die Start-URL dieser App eintragen. API-Berechtigungen: Microsoft Graph delegiert <code>Presence.Read</code> + <code>Presence.ReadWrite</code>, danach Administratorzustimmung erteilen.</li>
            <li>Webhook-Secret frei wählbar; öffentliche Basis-URL = öffentlich erreichbare Adresse des Bamoa-Backends.</li>
          </ol>
        ) : (
          <ol style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--muted, #8aa)', lineHeight: 1.55 }}>
            <li><a href="https://developer.webex.com" target="_blank" rel="noreferrer" style={{ color: 'var(--tenant-accent, #7c3aed)' }}>developer.webex.com</a> → Profilbild → <strong>My Webex Apps</strong> → <strong>Create a New App</strong> → <strong>Create an Integration</strong> (kein Admin nötig).</li>
            <li>Als <strong>Redirect URI</strong> die Start-URL dieser App eintragen (z. B. <code>https://agent.vs.bamoa.de/</code>, exakt inkl. <code>/</code>). Scopes: <code>spark:people_read</code> und <code>spark:people_write</code>.</li>
            <li>Auf <strong>Add Integration</strong> klicken → die Seite zeigt <strong>Client ID</strong> und <strong>Client Secret</strong>.</li>
            <li>Das <strong>Secret wird nur einmal angezeigt</strong> — sofort kopieren. (Verloren? Auf der Integrations-Seite neu erzeugen.)</li>
          </ol>
        )}
      </details>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <TpField label="Client ID" value={clientId} onChange={setClientId} />
        <TpField
          label={initial.hasSecret ? 'Client Secret — gespeichert, leer lassen zum Beibehalten' : 'Client Secret'}
          value={clientSecret} onChange={setClientSecret} type="password"
          placeholder={initial.hasSecret ? '••••••••' : ''}
        />
        <TpField label="Redirect-URI (Start-URL der Agenten-App)" value={redirectUri} onChange={setRedirectUri} placeholder="https://agent.vs.bamoa.de/" />
        {isGraph && <TpField label="Verzeichnis-(Mandanten-)ID" value={msTenantId} onChange={setMsTenantId} />}
        {isGraph && <TpField label="Öffentliche Basis-URL (für den Graph-Webhook)" value={publicBaseUrl} onChange={setPublicBaseUrl} placeholder="https://api.vs.bamoa.de" />}
        {!isGraph && <TpField label="Sync-Intervall in Sekunden (leer = 5 s; min. 2, max. 120)" value={pollSeconds} onChange={setPollSeconds} type="number" placeholder="5" />}
        {isGraph && (
          <TpField
            label={initial.hasWebhookSecret ? 'Webhook-Secret — gespeichert, leer lassen' : 'Webhook-Secret (frei wählbar)'}
            value={webhookSecret} onChange={setWebhookSecret} type="password"
            placeholder={initial.hasWebhookSecret ? '••••••••' : ''}
          />
        )}
      </div>
      <button onClick={save} disabled={busy} style={{ ...applyBtn, marginTop: 12 }}>Speichern</button>
    </div>
  );
}

// ── Anlagen-Konnektor (pre251: CSTA-Gateway, Admin-Freischaltung) ────────────
function ConnectorAdminPanel() {
  const [st, setSt] = useState<{ enabled: boolean; keySet: boolean; auxReasonCode: string; connected: number; providers: string[]; mappedUsers: number; adapter: { type: string; host?: string; port?: number; secure?: boolean; acd?: string } } | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try { const r = await apiFetch('/api/telephony-connector/admin/status'); if (r.ok) setSt(await r.json()); } catch { /* */ }
  }, []);
  useEffect(() => { void load(); const t = setInterval(() => void load(), 10000); return () => clearInterval(t); }, [load]);
  const save = async (patch: any) => {
    setMsg('');
    try {
      const r = await apiFetch('/api/telephony-connector/admin/config', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
      setMsg(r.ok ? 'Gespeichert.' : r.status === 403 ? 'Keine Berechtigung.' : 'Fehlgeschlagen.');
      void load();
    } catch { setMsg('Verbindung fehlgeschlagen.'); }
    setTimeout(() => setMsg(''), 3000);
  };
  const rotate = async () => {
    if (!window.confirm('Neuen Konnektor-Schlüssel erzeugen? Ein bereits verbundener Konnektor wird getrennt und muss mit dem neuen Schlüssel neu konfiguriert werden.')) return;
    try {
      const r = await apiFetch('/api/telephony-connector/admin/rotate-key', { method: 'POST' });
      if (r.ok) { const j = await r.json(); setNewKey(j.key); void load(); }
    } catch { setMsg('Fehlgeschlagen.'); }
  };
  return (
    <section style={card}>
      <div style={cardHead}>Telefonanlagen-Konnektor (Avaya, Estos & Co.)</div>
      <div style={settingHint}>
        <b>Was ist das?</b> Für Telefonanlagen ohne Cloud-Schnittstelle (z. B. Avaya Aura im
        Kundenservice-Center, Estos ProCall) wird ein kleiner Konnektor-Dienst im Hausnetz installiert.
        Er verbindet sich ausschließlich ausgehend und verschlüsselt mit Bamoa und koppelt den
        Telefonstatus in beide Richtungen: Telefonierende Berater erhalten keine Videocalls, und wer
        eine Videoberatung annimmt, wird für deren Dauer aus der Anrufverteilung (AUX) abgemeldet.
        {' '}<b>So richten Sie es ein:</b> (1) Hier freischalten und einen Schlüssel erzeugen. (2) Den
        Konnektor im Hausnetz mit diesem Schlüssel einrichten (Unterlage „Telefoniestatus-Kopplung mit
        Avaya Aura"). (3) Die Berater tragen ihre Nebenstelle selbst in ihren Einstellungen ein
        (Telefonie-Kopplung). Übertragen werden nur Zustandssignale — keine Gesprächsinhalte, keine
        Rufnummern von Kunden.
      </div>
      {!st ? <div style={settingHint}>Lade …</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={st.enabled} onChange={(e) => { setSt({ ...st, enabled: e.target.checked }); void save({ enabled: e.target.checked }); }} />
            Konnektor-Anbindung für diesen Mandanten freischalten
          </label>
          <div style={{ fontSize: 13 }}>
            Status:{' '}
            {st.connected > 0
              ? <b style={{ color: '#7ee787' }}>{st.connected} Konnektor verbunden ({st.providers.join(', ')})</b>
              : <b style={{ color: 'var(--muted,#8aa)' }}>kein Konnektor verbunden</b>}
            {' · '}{st.mappedUsers} Berater mit Nebenstelle
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => void rotate()} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--hairline,#333)', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 13 }}>
              {st.keySet ? 'Schlüssel neu erzeugen' : 'Konnektor-Schlüssel erzeugen'}
            </button>
            <label style={{ fontSize: 13 }}>AUX-Grundcode (optional):{' '}
              <input defaultValue={st.auxReasonCode} placeholder="z. B. 7" onBlur={(e) => void save({ auxReasonCode: e.target.value })}
                style={{ padding: '6px 8px', borderRadius: 6, background: 'var(--bg-elev-2)', color: 'inherit', border: '1px solid var(--hairline,#333)', width: 90 }} />
            </label>
          </div>
          <div style={{ borderTop: '1px solid var(--hairline,#333)', paddingTop: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 4 }}>Anlagen-Anbindung (wird an den Konnektor übertragen)</div>
            <div style={{ ...settingHint, marginBottom: 10 }}>
              Diese Einstellungen erhält der Konnektor automatisch beim Verbinden — Änderungen hier wirken
              sofort, ohne Neuinstallation im Hausnetz. Solange die technischen Details der Anlage noch
              nicht geklärt sind, kann alles auf „Simulator" bleiben und später einfach umgestellt werden.
              {' '}<b>Zugangsdaten der Anlage (CTI-Benutzer/Passwort) werden bewusst NICHT hier gepflegt</b> —
              sie werden lokal am Konnektor hinterlegt und nie an Bamoa übertragen.
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ fontSize: 13 }}>Anlagen-Typ:{' '}
                <select value={st.adapter.type}
                  onChange={(e) => { const adapter = { ...st.adapter, type: e.target.value }; setSt({ ...st, adapter }); void save({ adapter }); }}
                  style={{ padding: '6px 8px', borderRadius: 6, background: 'var(--bg-elev-2)', color: 'inherit', border: '1px solid var(--hairline,#333)' }}>
                  <option value="simulator">Simulator (Test, keine Anlage)</option>
                  <option value="avaya-dmcc">Avaya AES — DMCC (empfohlen)</option>
                  <option value="avaya-tsapi">Avaya AES — TSAPI</option>
                  <option value="estos-csta">Estos ProCall (CSTA)</option>
                </select>
              </label>
              <label style={{ fontSize: 13 }}>ACD:{' '}
                <select value={st.adapter.acd ?? 'elite'}
                  onChange={(e) => { const adapter = { ...st.adapter, acd: e.target.value }; setSt({ ...st, adapter }); void save({ adapter }); }}
                  style={{ padding: '6px 8px', borderRadius: 6, background: 'var(--bg-elev-2)', color: 'inherit', border: '1px solid var(--hairline,#333)' }}>
                  <option value="elite">Avaya Elite</option>
                  <option value="aacc">Avaya Aura Contact Center</option>
                  <option value="none">Keine ACD (nur Telefonstatus)</option>
                </select>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
              <label style={{ fontSize: 13 }}>Anlagen-/AES-Host:{' '}
                <input defaultValue={st.adapter.host ?? ''} placeholder="z. B. aes01.institut.local"
                  onBlur={(e) => { const adapter = { ...st.adapter, host: e.target.value.trim() }; void save({ adapter }); }}
                  style={{ padding: '6px 8px', borderRadius: 6, background: 'var(--bg-elev-2)', color: 'inherit', border: '1px solid var(--hairline,#333)', width: 210 }} />
              </label>
              <label style={{ fontSize: 13 }}>Port:{' '}
                <input defaultValue={st.adapter.port ?? ''} placeholder="4722" inputMode="numeric"
                  onBlur={(e) => { const adapter = { ...st.adapter, port: Number(e.target.value) || undefined }; void save({ adapter }); }}
                  style={{ padding: '6px 8px', borderRadius: 6, background: 'var(--bg-elev-2)', color: 'inherit', border: '1px solid var(--hairline,#333)', width: 80 }} />
              </label>
              <label style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={st.adapter.secure !== false}
                  onChange={(e) => { const adapter = { ...st.adapter, secure: e.target.checked }; setSt({ ...st, adapter }); void save({ adapter }); }} />
                TLS zur Anlage
              </label>
            </div>
          </div>
          {newKey && (
            <div style={{ fontSize: 12.5, background: 'var(--bg-elev-2,#20262b)', border: '1px solid var(--hairline,#333)', borderRadius: 8, padding: '10px 12px' }}>
              <b>Neuer Konnektor-Schlüssel (wird nur EINMAL angezeigt — jetzt sicher ablegen):</b>
              <div style={{ fontFamily: 'monospace', fontSize: 13, marginTop: 6, userSelect: 'all', wordBreak: 'break-all' }}>{newKey}</div>
            </div>
          )}
          {msg && <div style={{ fontSize: 12.5, color: 'var(--muted,#8aa)' }}>{msg}</div>}
        </div>
      )}
    </section>
  );
}

// ── Signatur-Ablauf (pre247: Berater-Review vor dem Siegeln) ─────────────────
function SignFlowPanel() {
  const [review, setReview] = useState<boolean | null>(null);
  const [msg, setMsg] = useState('');
  useEffect(() => { void apiFetch('/api/sign-settings/status').then(async (r) => { if (r.ok) { const j = await r.json(); setReview(!!j.reviewBeforeSeal); } }).catch(() => {}); }, []);
  const save = async (v: boolean) => {
    setReview(v); setMsg('');
    try {
      const r = await apiFetch('/api/sign-settings/config', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reviewBeforeSeal: v }) });
      setMsg(r.ok ? 'Gespeichert.' : r.status === 403 ? 'Keine Berechtigung.' : 'Speichern fehlgeschlagen.');
    } catch { setMsg('Verbindung fehlgeschlagen.'); }
    setTimeout(() => setMsg(''), 3000);
  };
  return (
    <section style={card}>
      <div style={cardHead}>Signatur-Ablauf</div>
      <div style={settingHint}>
        Ist das Review aktiv, wird das signierte Dokument (inkl. Protokollseite) dem Berater
        zunächst zur Prüfung angezeigt; erst sein Klick löst das Siegeln und die Ablage aus.
        Ohne Review wird automatisch gesiegelt, sobald das Ident-Bündel vollständig ist.
      </div>
      {review === null ? <div style={settingHint}>Lade …</div> : (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={review} onChange={(e) => void save(e.target.checked)} />
          Berater-Review vor dem Siegeln
        </label>
      )}
      {msg && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--muted,#8aa)' }}>{msg}</div>}
    </section>
  );
}

// ── SMS-OTP (pre239: Einmalcodes für das Ident-Bündel, provider-agnostisch) ──
function SmsOtpPanel() {
  const [cfg, setCfg] = useState<{ enabled: boolean; provider: string; apiKeySet: boolean; sender: string } | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [testPhone, setTestPhone] = useState('');

  const load = useCallback(async () => {
    try { const r = await apiFetch('/api/sms-otp/config'); if (r.ok) setCfg(await r.json()); } catch { /* */ }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const save = async (patch: Partial<{ enabled: boolean; provider: string; sender: string }>) => {
    if (!cfg) return;
    setBusy(true); setMsg('');
    try {
      const body: any = { enabled: cfg.enabled, provider: cfg.provider, sender: cfg.sender, ...patch };
      if (apiKey) body.apiKey = apiKey;
      const r = await apiFetch('/api/sms-otp/config', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (r.ok) { setMsg('Gespeichert.'); setApiKey(''); void load(); }
      else if (r.status === 403) setMsg('Keine Berechtigung.');
      else setMsg('Speichern fehlgeschlagen.');
    } catch { setMsg('Verbindung fehlgeschlagen.'); }
    finally { setBusy(false); setTimeout(() => setMsg(''), 3000); }
  };

  return (
    <section style={card}>
      <div style={cardHead}>SMS-Einmalcodes (Signatur-Identifizierung)</div>
      <div style={settingHint}>
        Sendet dem Kunden vor der Unterschrift einen 6-stelligen Code an die vom Berater
        eingegebene Mobilnummer (Ident-Bündel). Datensparsam: Nur Nummer + Code gehen an den
        Anbieter; Nummern werden nicht gespeichert, Codes nur als Hash (5 Min. gültig).
        Anbieter mit AVV verwenden (z. B. seven.io); weitere Anbieter folgen.
      </div>
      {!cfg ? <div style={settingHint}>Lade …</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={cfg.enabled} disabled={busy}
              onChange={(e) => { setCfg({ ...cfg, enabled: e.target.checked }); void save({ enabled: e.target.checked }); }} />
            SMS-Codes für diesen Mandanten aktivieren
          </label>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ fontSize: 13 }}>Anbieter:{' '}
              <select value={cfg.provider} disabled={busy}
                onChange={(e) => { setCfg({ ...cfg, provider: e.target.value }); void save({ provider: e.target.value }); }}
                style={{ padding: '6px 8px', borderRadius: 6, background: 'var(--bg-elev-2)', color: 'inherit', border: '1px solid var(--hairline,#333)' }}>
                <option value="debug">Debug (kein Versand — nur Test)</option>
                <option value="seven">seven.io (Deutschland, AVV)</option>
              </select>
            </label>
            <label style={{ fontSize: 13 }}>Absender:{' '}
              <input value={cfg.sender} disabled={busy} maxLength={11} placeholder="Signatur"
                onChange={(e) => setCfg({ ...cfg, sender: e.target.value })}
                onBlur={() => void save({})}
                style={{ padding: '6px 8px', borderRadius: 6, background: 'var(--bg-elev-2)', color: 'inherit', border: '1px solid var(--hairline,#333)', width: 130 }} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder={cfg.apiKeySet ? 'API-Key gesetzt — neuen eingeben zum Ersetzen' : 'API-Key des Anbieters'}
              style={{ padding: '7px 10px', borderRadius: 6, background: 'var(--bg-elev-2)', color: 'inherit', border: '1px solid var(--hairline,#333)', minWidth: 280, fontSize: 13 }} />
            <button onClick={() => void save({})} disabled={busy || !apiKey}
              style={{ padding: '7px 12px', borderRadius: 8, border: 'none', background: 'var(--accent,#e2001a)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>API-Key speichern</button>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+49 170 1234567"
              style={{ padding: '7px 10px', borderRadius: 6, background: 'var(--bg-elev-2)', color: 'inherit', border: '1px solid var(--hairline,#333)', minWidth: 180, fontSize: 13 }} />
            <button disabled={busy || !testPhone} onClick={() => { setBusy(true); setMsg('Sende Test-SMS …');
              void apiFetch('/api/sms-otp/test', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone: testPhone }) })
                .then(async (r) => { const j = await r.json().catch(() => ({})); setMsg(j.ok ? `✓ ${j.message}` : `✗ ${j.message ?? 'Fehler'}`); })
                .catch(() => setMsg('✗ Verbindung fehlgeschlagen'))
                .finally(() => { setBusy(false); setTimeout(() => setMsg(''), 12000); }); }}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--hairline,#333)', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 13 }}>Test-SMS senden</button>
          </div>
          {msg && <div style={{ fontSize: 12.5, color: 'var(--muted,#8aa)' }}>{msg}</div>}
        </div>
      )}
    </section>
  );
}

// ── Siegel-Zertifikat (Stage B: zentral, verschlüsselt) ─────────────────────
function SealCertPanel() {
  const [status, setStatus] = useState<{ label: string; uploadedAt: string } | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch('/api/seal-cert/status');
      if (r.ok) { const j = await r.json(); setStatus({ label: j.label, uploadedAt: j.uploadedAt }); }
      else setStatus(null);
    } catch { setStatus(null); }
    finally { setLoaded(true); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const upload = async (file: File) => {
    setBusy(true); setMsg('');
    try {
      const p12Pass = window.prompt('Passwort der P12/PFX-Datei:') ?? '';
      // Label client-seitig aus dem Zertifikat lesen (validiert zugleich das P12-Passwort)
      const p12 = await file.arrayBuffer();
      const { importP12ToStore, clearStoredSealKey } = await import('@/lib/sealCryptoKey');
      let label = file.name;
      try { label = await importP12ToStore(p12, p12Pass); await clearStoredSealKey(); }
      catch { setMsg('P12-Passwort falsch oder Datei ungültig.'); setBusy(false); return; }
      // pre254: Passphrase-Mindestanforderung (beschlossene Härtung, docs/02 §4.3).
      // Die Passphrase ist der Sicherheitsanker gegen Offline-Brute-Force auf das
      // Chiffrat — sie muss wie ein Schlüssel behandelt werden, nicht wie ein Login.
      const askTenantPass = (): string | null => {
        for (;;) {
          const p = window.prompt(
            'Mandanten-Passphrase festlegen (Berater benötigen sie zum Entschlüsseln).\n\n'
            + 'Anforderung: mindestens 12 Zeichen.\n'
            + 'Empfehlung: 16+ zufällige Zeichen ODER 4+ zufällige Wörter —\n'
            + 'die Passphrase schützt das Siegel-Zertifikat gegen Brute-Force.'
          );
          if (p == null) return null;
          if (p.length < 12) { window.alert('Zu kurz (' + p.length + ' Zeichen) — mindestens 12 Zeichen erforderlich.'); continue; }
          const words = p.trim().split(/[\s-]+/).filter((w) => w.length >= 3).length;
          const classes = [/[a-zäöüß]/, /[A-ZÄÖÜ]/, /[0-9]/, /[^A-Za-z0-9äöüßÄÖÜ]/].filter((r) => r.test(p)).length;
          const strong = p.length >= 16 || words >= 4 || (p.length >= 14 && classes >= 3);
          if (!strong && !window.confirm('Die Passphrase erfüllt das Minimum, ist aber schwächer als empfohlen (16+ Zeichen oder 4+ Wörter).\nTrotzdem verwenden?')) continue;
          return p;
        }
      };
      const tenantPass = askTenantPass() ?? '';
      if (!tenantPass) { setMsg('Abgebrochen.'); setBusy(false); return; }
      if (tenantPass !== p12Pass) {
        setMsg('Hinweis: Passphrasen unterschiedlich — Berater brauchen dann beide nacheinander. Upload läuft.');
      }
      const { encryptP12 } = await import('@/lib/sealCertCrypto');
      const enc = await encryptP12(p12, tenantPass);
      const r = await apiFetch('/api/seal-cert', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...enc, label }),
      });
      if (r.ok) { setMsg('Hochgeladen — Server speichert nur das Chiffrat.'); void load(); }
      else if (r.status === 403) setMsg('Keine Berechtigung (nur Mandanten-Admins).');
      else setMsg('Upload fehlgeschlagen.');
    } catch (e) { console.error('[seal-admin]', e); setMsg('Fehler beim Verschlüsseln/Hochladen.'); }
    finally { setBusy(false); setTimeout(() => setMsg(''), 6000); }
  };

  const remove = async () => {
    if (!window.confirm('Zentrales Siegel-Zertifikat entfernen?')) return;
    setBusy(true);
    try { const r = await apiFetch('/api/seal-cert', { method: 'DELETE' }); if (r.ok) { setStatus(null); setMsg('Entfernt.'); } }
    catch { setMsg('Entfernen fehlgeschlagen.'); }
    finally { setBusy(false); setTimeout(() => setMsg(''), 3000); }
  };

  return (
    <section style={card}>
      <div style={cardHead}>Siegel-Zertifikat (PAdES)</div>
      <div style={settingHint}>
        Zentrales Zertifikat für das automatische Versiegeln signierter Dokumente. Es wird im
        Browser mit einer Mandanten-Passphrase verschlüsselt — der Bamoa-Server speichert nur
        das Chiffrat und kann es nicht lesen. Berater entschlüsseln es lokal mit der Passphrase;
        der Schlüssel wird dort nicht-exportierbar abgelegt.
      </div>
      {!loaded ? <div style={settingHint}>Lade …</div> : status ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13.5 }}>
            {'🔒'} <b>{status.label}</b>
            <span style={{ opacity: 0.65 }}> · hinterlegt {new Date(status.uploadedAt).toLocaleString('de-DE')}</span>
          </span>
          <button onClick={() => fileRef.current?.click()} disabled={busy} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--hairline,#333)', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 13 }}>Ersetzen</button>
          <button onClick={() => void remove()} disabled={busy} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #a33', background: 'transparent', color: '#e88', cursor: 'pointer', fontSize: 13 }}>Entfernen</button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--warning, #ffb74d)' }}>Kein zentrales Zertifikat hinterlegt.</span>
          <button onClick={() => fileRef.current?.click()} disabled={busy} style={{ padding: '9px 14px', borderRadius: 8, border: 'none', background: 'var(--accent, #e2001a)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13.5 }}>
            {busy ? 'Verschlüssele & lade hoch …' : 'P12/PFX hochladen (verschlüsselt)'}
          </button>
        </div>
      )}
      <input ref={fileRef} type="file" accept=".p12,.pfx,application/x-pkcs12" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void upload(f); }} />
      {msg && <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--muted,#8aa)' }}>{msg}</div>}
    </section>
  );
}

function TelephonyPanel() {
  const [configs, setConfigs] = useState<TpConfig[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await apiFetch('/api/tenant-admin/telephony-config');
      if (r.ok) { const j = await r.json(); setConfigs(j.configs || []); }
      else setMsg('Konfiguration konnte nicht geladen werden.');
    } catch { setMsg('Konfiguration konnte nicht geladen werden.'); }
    finally { setLoaded(true); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  return (
    <section style={card}>
      <div style={cardHead}>Telefonie-Kopplung</div>
      <div style={settingHint}>
        Verbindet den Telefoniestatus der Berater automatisch mit dem Video-Routing. Tragen Sie pro Anbieter die
        Werte aus der jeweiligen App-Registrierung (Webex-Integration bzw. Microsoft-Entra-App) ein. Secrets werden
        nur gespeichert und nie wieder angezeigt; ein leeres Secret-Feld behält den vorhandenen Wert. Aktivieren Sie
        einen Anbieter erst, wenn Client ID, Secret und Redirect-URI hinterlegt sind.
      </div>
      {!loaded ? (
        <div style={mutedLine}>Lädt…</div>
      ) : (
        configs.map((c) => (
          <TpProviderForm key={c.provider} initial={c} onSaved={(m) => { setMsg(m); void load(); setTimeout(() => setMsg(''), 2500); }} />
        ))
      )}
      {msg && <div style={{ marginTop: 4, fontSize: 12.5, color: 'var(--tenant-accent, #7c3aed)' }}>{msg}</div>}
    </section>
  );
}

// ── Aufzeichnung (Welle 7e) ─────────────────────────────────────────────────
function RecordingPolicyPanel() {
  const [p, setP] = useState<RecordingPolicy>(DEFAULT_POLICY);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    apiFetch('/api/recording/policy', {}, { noLogoutOn401: true } as any)
      .then((r) => (r.ok ? r.json() : null))
      .then((x) => { if (x) setP(normalizePolicy(x)); })
      .catch(() => setMsg('Aktuelle Einstellung konnte nicht geladen werden.'))
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await apiFetch('/api/recording/policy', {
        method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(p),
      });
      if (r.ok) { setP(normalizePolicy(await r.json())); setMsg('Gespeichert.'); }
      else if (r.status === 403) setMsg('Keine Berechtigung für diese Änderung.');
      else setMsg('Speichern fehlgeschlagen.');
    } catch { setMsg('Verbindung fehlgeschlagen.'); }
    finally { setBusy(false); setTimeout(() => setMsg(''), 2500); }
  };

  const set = (patch: Partial<RecordingPolicy>) => setP((cur) => ({ ...cur, ...patch }));
  const setTopic = (i: number, patch: Partial<RecordingTopic>) =>
    setP((cur) => ({ ...cur, topics: cur.topics.map((t, idx) => (idx === i ? { ...t, ...patch } : t)) }));
  const removeTopic = (i: number) => setP((cur) => ({ ...cur, topics: cur.topics.filter((_, idx) => idx !== i) }));
  const addTopic = () => setP((cur) => ({
    ...cur,
    topics: [...cur.topics, { id: `thema-${Date.now()}`, label: 'Neues Thema', enabled: true, consentMode: 'einwilligung', deliveryMode: 'local-folder' }],
  }));
  const dis = busy || !loaded;

  const miniSeg = (active: boolean): CSSProperties => ({
    flex: 1, padding: '6px 8px', fontSize: 12, borderRadius: 6, cursor: dis ? 'default' : 'pointer',
    border: active ? '1px solid var(--tenant-accent, #7c3aed)' : '1px solid #ccc',
    background: active ? 'var(--tenant-accent, #7c3aed)' : '#fff',
    color: active ? '#fff' : 'inherit', fontWeight: active ? 600 : 500,
  });

  return (
    <section style={card}>
      <div style={cardHead}>Aufzeichnung</div>

      <div style={{ marginBottom: 22 }}>
        <div style={settingTitle}>Aufzeichnung aktiv</div>
        <div style={settingHint}>
          Master-Schalter für diesen Mandanten. Bei <strong>Aus</strong> bietet die Berater-App keine
          Aufzeichnung an. Die Aufnahme entsteht stets client-seitig beim Berater und verlässt den Client
          nur verschlüsselt – der Bamoa-Server verarbeitet keinen Klartext.
        </div>
        <div style={segRow}>
          <button style={seg(p.enabled)} disabled={dis} onClick={() => set({ enabled: true })}>An</button>
          <button style={seg(!p.enabled)} disabled={dis} onClick={() => set({ enabled: false })}>Aus</button>
        </div>
      </div>

      <div style={{ marginBottom: 22 }}>
        <div style={settingTitle}>Beratungsthemen</div>
        <div style={settingHint}>
          Frei editierbare Liste. Pro Thema getrennt wählbar: Zustimmungs-Modus
          (<strong>Hinweis</strong> nach MiFID II vs. aktive <strong>Einwilligung</strong> nach § 201 StGB)
          und Ablage-Modus (lokaler Ordner vs. Server-Relay).
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
          {p.topics.map((t, i) => (
            <div key={t.id} style={{ border: '1px solid #e3e3ee', borderRadius: 10, padding: 12, opacity: t.enabled ? 1 : 0.6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <input
                  type="text" value={t.label} disabled={dis}
                  onChange={(e) => setTopic(i, { label: e.target.value })}
                  placeholder="Themenname"
                  style={{ flex: 1, padding: '8px 10px', borderRadius: 7, border: '1px solid #ccc', fontSize: 13.5, fontWeight: 600 }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={t.enabled} disabled={dis} onChange={(e) => setTopic(i, { enabled: e.target.checked })} />
                  aktiv
                </label>
                <button onClick={() => removeTopic(i)} disabled={dis} title="Thema entfernen"
                  style={{ border: 'none', background: 'transparent', cursor: dis ? 'default' : 'pointer', fontSize: 18, color: '#b00', lineHeight: 1, padding: '0 4px' }}>×</button>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px' }}>
                  <div style={{ fontSize: 11.5, color: 'var(--muted, #8aa)', marginBottom: 5 }}>Zustimmung</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={miniSeg(t.consentMode === 'hinweis')} disabled={dis} onClick={() => setTopic(i, { consentMode: 'hinweis' })}>Hinweis</button>
                    <button style={miniSeg(t.consentMode === 'einwilligung')} disabled={dis} onClick={() => setTopic(i, { consentMode: 'einwilligung' })}>Einwilligung</button>
                  </div>
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <div style={{ fontSize: 11.5, color: 'var(--muted, #8aa)', marginBottom: 5 }}>Ablage</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={miniSeg(t.deliveryMode === 'local-folder')} disabled={dis} onClick={() => setTopic(i, { deliveryMode: 'local-folder' })}>Lokaler Ordner</button>
                    <button style={miniSeg(t.deliveryMode === 'server-relay')} disabled={dis} onClick={() => setTopic(i, { deliveryMode: 'server-relay' })}>Server-Relay</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button onClick={addTopic} disabled={dis}
            style={{ alignSelf: 'flex-start', padding: '8px 14px', borderRadius: 8, border: '1px dashed var(--tenant-accent, #7c3aed)', background: 'transparent', color: 'var(--tenant-accent, #7c3aed)', fontSize: 13, fontWeight: 600, cursor: dis ? 'default' : 'pointer' }}>
            + Thema hinzufügen
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 22 }}>
        <div style={settingTitle}>Empfänger-Public-Key (PGP)</div>
        <div style={settingHint}>
          Öffentlicher Schlüssel des Zielarchivs. Das Paket wird client-seitig nur hierauf verschlüsselt – Bamoa
          kann es nie lesen. Leer = kein Schlüssel (für Server-Relay erforderlich).
        </div>
        <textarea
          value={p.recipientPublicKey ?? ''} disabled={dis}
          onChange={(e) => set({ recipientPublicKey: e.target.value.trim() ? e.target.value : null })}
          placeholder="-----BEGIN PGP PUBLIC KEY BLOCK-----"
          style={{ width: '100%', minHeight: 90, marginTop: 10, padding: '9px 10px', borderRadius: 8, border: '1px solid #ccc', fontSize: 12, fontFamily: 'monospace', resize: 'vertical' }}
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={settingTitle}>Aufbewahrungsdauer (Jahre)</div>
        <div style={settingHint}>Hinweis für das Zielarchiv; typischerweise 5–7 Jahre (WpHG/MiFID II).</div>
        <input
          type="number" min={1} max={30} value={p.retentionYears} disabled={dis}
          onChange={(e) => set({ retentionYears: Math.max(1, Math.min(30, Number(e.target.value) || 5)) })}
          style={{ width: 100, marginTop: 10, padding: '9px 10px', borderRadius: 8, border: '1px solid #ccc', fontSize: 14 }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 18 }}>
        <button onClick={save} disabled={dis} style={{ ...seg(true), opacity: dis ? 0.6 : 1, cursor: dis ? 'default' : 'pointer' }}>
          {busy ? 'Speichern…' : 'Speichern'}
        </button>
        {msg && <span style={{ fontSize: 12.5, color: 'var(--tenant-accent, #7c3aed)' }}>{msg}</span>}
      </div>
    </section>
  );
}


// ── Übersicht: Berater & Gruppen ────────────────────────────────────────────
interface Adviser { id: string; displayName: string; status: string; groups: { id: string; name: string }[] }
interface Group { id: string; name: string; total: number; available: number }

const STATUS_DOT: Record<string, string> = { available: '#22C55E', pause: '#F59E0B', wrap: '#3B82F6', offline: '#6B7280' };

function OverviewPanel() {
  const [advisers, setAdvisers] = useState<Adviser[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([
      apiFetch('/api/advisers').then((r) => (r.ok ? r.json() : [])),
      apiFetch('/api/groups').then((r) => (r.ok ? r.json() : [])),
    ]).then(([a, g]) => {
      if (!alive) return;
      setAdvisers(Array.isArray(a) ? a : []);
      setGroups(Array.isArray(g) ? g : []);
      setLoading(false);
    }).catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const available = advisers.filter((a) => a.status === 'available').length;

  return (
    <section style={card}>
      <div style={cardHead}>Übersicht</div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <Stat label="Berater" value={String(advisers.length)} />
        <Stat label="Verfügbar" value={String(available)} accent />
        <Stat label="Gruppen" value={String(groups.length)} />
      </div>

      <div style={settingTitle}>Berater</div>
      {loading ? <div style={mutedLine}>Wird geladen…</div> : advisers.length === 0 ? <div style={mutedLine}>Keine Berater.</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 18 }}>
          {advisers.map((a) => (
            <div key={a.id} style={listRow}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_DOT[a.status] || STATUS_DOT.offline }} />
                {a.displayName}
              </span>
              <span style={{ fontSize: 12, color: 'var(--muted, #8aa)' }}>
                {a.groups.map((g) => g.name).join(', ') || '—'}
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={settingTitle}>Gruppen</div>
      {loading ? <div style={mutedLine}>Wird geladen…</div> : groups.length === 0 ? <div style={mutedLine}>Keine Gruppen.</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groups.map((g) => (
            <div key={g.id} style={listRow}>
              <span>{g.name}</span>
              <span style={{ fontSize: 12, color: 'var(--muted, #8aa)' }}>{g.available}/{g.total} verfügbar</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{ flex: 1, padding: '12px 14px', borderRadius: 10, background: 'var(--bg-elev-2)', border: '1px solid var(--hairline, #232323)' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: accent ? 'var(--tenant-accent, #7c3aed)' : 'inherit' }}>{value}</div>
      <div style={{ fontSize: 11.5, color: 'var(--muted, #8aa)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

// ── Statistik / Auswertungen (Welle 10) ─────────────────────────────────────
type StatsFilter = { from: string; to: string; groupId: string; agentId: string; steleId: string };

function isoDay(d: Date) { return d.toISOString().slice(0, 10); }
function defaultFilter(): StatsFilter {
  const to = new Date(); const from = new Date(); from.setDate(from.getDate() - 29);
  return { from: isoDay(from), to: isoDay(to), groupId: '', agentId: '', steleId: '' };
}
function fmtSec(s: number | null | undefined): string {
  if (s == null) return '–';
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60); const r = Math.round(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
}
const nval = (x: any, suffix = '') => (x == null ? '–' : `${x}${suffix}`);

function StatsPanel() {
  const [filter, setFilter] = useState<StatsFilter>(defaultFilter);
  const [groups, setGroups] = useState<any[]>([]);
  const [advisers, setAdvisers] = useState<any[]>([]);
  const [steles, setSteles] = useState<any[]>([]);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // Filter-Datenquellen einmalig laden
  useEffect(() => {
    Promise.all([
      apiFetch('/api/groups').then((r) => (r.ok ? r.json() : [])).catch(() => []),
      apiFetch('/api/advisers').then((r) => (r.ok ? r.json() : [])).catch(() => []),
      apiFetch('/api/tenant-admin/steles').then((r) => (r.ok ? r.json() : [])).catch(() => []),
    ]).then(([g, a, s]) => {
      setGroups(Array.isArray(g) ? g : []);
      setAdvisers(Array.isArray(a) ? a : []);
      setSteles(Array.isArray(s) ? s : []);
    });
  }, []);

  const qs = (extra?: Record<string, string>) => {
    const p = new URLSearchParams();
    if (filter.from) p.set('from', filter.from);
    if (filter.to) p.set('to', `${filter.to}T23:59:59`);
    if (filter.groupId) p.set('groupId', filter.groupId);
    if (filter.agentId) p.set('agentId', filter.agentId);
    if (filter.steleId) p.set('steleId', filter.steleId);
    for (const [k, v] of Object.entries(extra ?? {})) p.set(k, v);
    return p.toString();
  };

  const load = async () => {
    setLoading(true); setErr('');
    const reports = ['summary', 'timeseries', 'by-group', 'by-agent', 'by-stele', 'ratings', 'quality', 'quality-by-agent', 'quality-by-stele', 'topics', 'heatmap'];
    try {
      const results = await Promise.all(reports.map((r) =>
        apiFetch(`/api/stats/${r}?${qs(r === 'timeseries' ? { bucket: 'day' } : undefined)}`, {}, { noLogoutOn401: true } as any)
          .then((res) => (res.ok ? res.json() : null)).catch(() => null)));
      const [summary, series, byGroup, byAgent, byStele, ratings, quality, qualAgent, qualStele, topics, heatmap] = results;
      if (!summary) { setErr('Auswertungen konnten nicht geladen werden (Backend aktuell?).'); }
      setData({ summary, series, byGroup, byAgent, byStele, ratings, quality, qualAgent, qualStele, topics, heatmap });
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, []);

  const downloadCsv = async (report: string) => {
    try {
      const res = await apiFetch(`/api/stats/${report}?${qs({ format: 'csv', ...(report === 'timeseries' ? { bucket: 'day' } : {}) })}`, {}, { noLogoutOn401: true } as any);
      if (!res.ok) return;
      const text = await res.text();
      const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a'); a.href = url; a.download = `${report}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch { /* ignore */ }
  };

  const set = (patch: Partial<StatsFilter>) => setFilter((f) => ({ ...f, ...patch }));
  const s = data?.summary;

  return (
    <section style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={cardHead}>Statistik</div>
        {loading && <span style={{ fontSize: 12, color: 'var(--muted, #8aa)' }}>lädt…</span>}
      </div>

      {/* Filterleiste */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 18 }}>
        <Field label="Von"><input type="date" value={filter.from} onChange={(e) => set({ from: e.target.value })} style={statInput} /></Field>
        <Field label="Bis"><input type="date" value={filter.to} onChange={(e) => set({ to: e.target.value })} style={statInput} /></Field>
        <Field label="Gruppe">
          <select value={filter.groupId} onChange={(e) => set({ groupId: e.target.value })} style={statInput}>
            <option value="">alle</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </Field>
        <Field label="Berater">
          <select value={filter.agentId} onChange={(e) => set({ agentId: e.target.value })} style={statInput}>
            <option value="">alle</option>
            {advisers.map((a) => <option key={a.id} value={a.id}>{a.displayName}</option>)}
          </select>
        </Field>
        <Field label="Stele">
          <select value={filter.steleId} onChange={(e) => set({ steleId: e.target.value })} style={statInput}>
            <option value="">alle</option>
            {steles.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
          </select>
        </Field>
        <button onClick={load} disabled={loading} style={applyBtn}>Anwenden</button>
      </div>

      {err && <div style={{ fontSize: 12.5, color: '#c0392b', marginBottom: 12 }}>{err}</div>}

      {/* KPI-Kacheln */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 22 }}>
        <Kpi label="Calls" value={nval(s?.totalCalls)} />
        <Kpi label="Angenommen" value={nval(s?.accepted)} />
        <Kpi label="Annahmequote" value={nval(s?.acceptRate, '%')} accent />
        <Kpi label="Verpasst" value={nval(s?.missed)} />
        <Kpi label="Ø Wartezeit" value={fmtSec(s?.avgWaitSec)} />
        <Kpi label="Ø Dauer" value={fmtSec(s?.avgHandleSec)} />
        <Kpi label="Gesprächsmin." value={nval(s?.totalTalkMinutes)} />
        <Kpi label="Ø Gast ★" value={nval(s?.avgGuestRating)} />
        <Kpi label="Ø Berater ★" value={nval(s?.avgAgentQuality)} />
        <Kpi label="Lösungsquote" value={nval(s?.resolutionRate, '%')} />
      </div>

      {/* Verlauf */}
      <Block title="Verlauf (Calls / Tag)" onCsv={() => downloadCsv('timeseries')}>
        <Timeseries rows={data?.series ?? []} />
      </Block>

      {/* Bewertungen beidseitig */}
      <Block title="Bewertungen" onCsv={() => downloadCsv('ratings')}>
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          <RatingHist title={`Gast (Ø ${nval(data?.ratings?.guest?.avg)}, n=${data?.ratings?.guest?.count ?? 0})`} hist={data?.ratings?.guest?.histogram} />
          <RatingHist title={`Berater (Ø ${nval(data?.ratings?.agent?.avg)}, n=${data?.ratings?.agent?.count ?? 0})`} hist={data?.ratings?.agent?.histogram} />
        </div>
      </Block>

      {/* Qualität / Netzwerk */}
      <Block title="Qualität & Netzwerk (gesamt)" onCsv={() => downloadCsv('quality')}>
        <QualityBlock q={data?.quality} />
      </Block>
      <Block title="Qualität je Berater (Berater-Seite — Probleme oben)" onCsv={() => downloadCsv('quality-by-agent')}>
        <QualityDimTable rows={data?.qualAgent ?? []} />
      </Block>
      <Block title="Qualität je Stele (Stele-Seite — Probleme oben)" onCsv={() => downloadCsv('quality-by-stele')}>
        <QualityDimTable rows={data?.qualStele ?? []} />
      </Block>

      {/* Tabellen */}
      <Block title="Nach Gruppe" onCsv={() => downloadCsv('by-group')}><DimTable rows={data?.byGroup ?? []} /></Block>
      <Block title="Nach Berater" onCsv={() => downloadCsv('by-agent')}><DimTable rows={data?.byAgent ?? []} /></Block>
      <Block title="Nach Stele" onCsv={() => downloadCsv('by-stele')}><DimTable rows={data?.byStele ?? []} /></Block>

      {/* Themen */}
      <Block title="Themen" onCsv={() => downloadCsv('topics')}><TopicBars rows={data?.topics ?? []} /></Block>

      {/* Heatmap */}
      <Block title="Stoßzeiten (Wochentag × Stunde)" onCsv={() => downloadCsv('heatmap')}><Heatmap hm={data?.heatmap} /></Block>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, color: 'var(--muted, #8aa)' }}>{label}{children}</label>;
}
function Kpi({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--bg-elev-2)', border: '1px solid var(--hairline, #232323)' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent ? 'var(--tenant-accent, #7c3aed)' : 'inherit' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--muted, #8aa)', marginTop: 2 }}>{label}</div>
    </div>
  );
}
function Block({ title, onCsv, children }: { title: string; onCsv: () => void; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={settingTitle}>{title}</div>
        <button onClick={onCsv} style={csvBtn} title="Als CSV exportieren">CSV</button>
      </div>
      {children}
    </div>
  );
}
function Timeseries({ rows }: { rows: any[] }) {
  if (!rows?.length) return <div style={mutedLine}>Keine Daten im Zeitraum.</div>;
  const W = 640, H = 120, pad = 6;
  const max = Math.max(1, ...rows.map((r) => r.calls));
  const bw = (W - pad * 2) / rows.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
      {rows.map((r, i) => {
        const h = Math.round(((H - pad * 2) * r.calls) / max);
        return <rect key={i} x={pad + i * bw + 1} y={H - pad - h} width={Math.max(1, bw - 2)} height={h} rx={1.5} fill="var(--tenant-accent, #7c3aed)" opacity={0.85} />;
      })}
      <text x={pad} y={12} fontSize="10" fill="var(--muted, #8aa)">max {max}/Tag · {rows.length} Tage</text>
    </svg>
  );
}
function RatingHist({ title, hist }: { title: string; hist?: Record<string, number> }) {
  const h = hist ?? { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  const max = Math.max(1, ...Object.values(h));
  return (
    <div style={{ flex: '1 1 240px', minWidth: 220 }}>
      <div style={{ fontSize: 12, marginBottom: 6, color: 'var(--muted, #8aa)' }}>{title}</div>
      {[5, 4, 3, 2, 1].map((star) => (
        <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <span style={{ width: 28, fontSize: 12 }}>{star}★</span>
          <div style={{ flex: 1, background: 'var(--bg-elev-2)', borderRadius: 4, height: 14, overflow: 'hidden' }}>
            <div style={{ width: `${((h[star] ?? 0) / max) * 100}%`, height: '100%', background: 'var(--tenant-accent, #7c3aed)', opacity: 0.85 }} />
          </div>
          <span style={{ width: 28, fontSize: 11, textAlign: 'right', color: 'var(--muted, #8aa)' }}>{h[star] ?? 0}</span>
        </div>
      ))}
    </div>
  );
}
function QualityBlock({ q }: { q: any }) {
  if (!q) return <div style={mutedLine}>Keine Qualitätsdaten.</div>;
  const v = q.verdicts ?? { good: 0, fair: 0, bad: 0 };
  const tot = Math.max(1, v.good + v.fair + v.bad);
  const seg = (n: number, color: string) => <div style={{ width: `${(n / tot) * 100}%`, background: color, height: '100%' }} />;
  const sideRow = (name: string, sd: any) => (
    <tr>
      <td style={td}>{name}</td><td style={tdR}>{nval(sd?.avgRttMs, ' ms')}</td><td style={tdR}>{nval(sd?.p95RttMs, ' ms')}</td>
      <td style={tdR}>{nval(sd?.avgJitterMs, ' ms')}</td><td style={tdR}>{nval(sd?.avgLossPct, '%')}</td>
      <td style={tdR}>{nval(sd?.avgFreezes)}</td><td style={tdR}>{nval(sd?.avgFps)}</td>
    </tr>
  );
  return (
    <div>
      <div style={{ display: 'flex', height: 16, borderRadius: 4, overflow: 'hidden', marginBottom: 6, background: 'var(--bg-elev-2)' }}>
        {seg(v.good, '#2e9e5b')}{seg(v.fair, '#e0a32e')}{seg(v.bad, '#c0392b')}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--muted, #8aa)', marginBottom: 12 }}>
        gut {v.good} · mittel {v.fair} · schlecht {v.bad} {q.badSharePct != null ? `(${q.badSharePct}% schlecht)` : ''}
      </div>
      <table style={tbl}>
        <thead><tr><th style={th}>Seite</th><th style={thR}>Ø RTT</th><th style={thR}>p95 RTT</th><th style={thR}>Ø Jitter</th><th style={thR}>Ø Loss</th><th style={thR}>Ø Freezes</th><th style={thR}>Ø FPS</th></tr></thead>
        <tbody>{sideRow('Berater', q.agent)}{sideRow('Stele', q.stele)}</tbody>
      </table>
      {Array.isArray(q.codecs) && q.codecs.length > 0 && (
        <div style={{ fontSize: 11.5, color: 'var(--muted, #8aa)', marginTop: 8 }}>Codecs: {q.codecs.map((c: any) => `${c.codec} (${c.n})`).join(' · ')}</div>
      )}
    </div>
  );
}
function qTone(v: number | null | undefined, warn: number, bad: number): CSSProperties {
  if (v == null) return {};
  if (v >= bad) return { color: '#e05a4d', fontWeight: 700 };
  if (v >= warn) return { color: '#e0a32e', fontWeight: 600 };
  return {};
}
function QualityDimTable({ rows }: { rows: any[] }) {
  if (!rows?.length) return <div style={mutedLine}>Keine Qualitätsdaten im Zeitraum.</div>;
  return (
    <table style={tbl}>
      <thead><tr>
        <th style={th}>Name</th><th style={thR}>Calls</th><th style={thR}>schlecht%</th>
        <th style={thR}>Ø RTT</th><th style={thR}>p95 RTT</th><th style={thR}>Ø Jitter</th>
        <th style={thR}>Ø Loss</th><th style={thR}>Ø Freezes</th><th style={thR}>Ø FPS</th>
      </tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td style={td}>{r.name}</td>
            <td style={tdR}>{r.calls}</td>
            <td style={{ ...tdR, ...qTone(r.badSharePct, 5, 20) }}>{nval(r.badSharePct, '%')}</td>
            <td style={{ ...tdR, ...qTone(r.avgRttMs, 150, 300) }}>{nval(r.avgRttMs, ' ms')}</td>
            <td style={{ ...tdR, ...qTone(r.p95RttMs, 200, 400) }}>{nval(r.p95RttMs, ' ms')}</td>
            <td style={{ ...tdR, ...qTone(r.avgJitterMs, 30, 60) }}>{nval(r.avgJitterMs, ' ms')}</td>
            <td style={{ ...tdR, ...qTone(r.avgLossPct, 0.5, 2) }}>{nval(r.avgLossPct, '%')}</td>
            <td style={{ ...tdR, ...qTone(r.avgFreezes, 0.5, 2) }}>{nval(r.avgFreezes)}</td>
            <td style={tdR}>{nval(r.avgFps)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function DimTable({ rows }: { rows: any[] }) {
  if (!rows?.length) return <div style={mutedLine}>Keine Daten im Zeitraum.</div>;
  return (
    <table style={tbl}>
      <thead><tr><th style={th}>Name</th><th style={thR}>Calls</th><th style={thR}>Quote</th><th style={thR}>Ø Wart.</th><th style={thR}>Ø Dauer</th><th style={thR}>Gast★</th><th style={thR}>Ber.★</th><th style={thR}>Lösung</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td style={td}>{r.name}</td><td style={tdR}>{r.calls}</td><td style={tdR}>{nval(r.acceptRate, '%')}</td>
            <td style={tdR}>{fmtSec(r.avgWaitSec)}</td><td style={tdR}>{fmtSec(r.avgHandleSec)}</td>
            <td style={tdR}>{nval(r.avgGuestRating)}</td><td style={tdR}>{nval(r.avgAgentQuality)}</td><td style={tdR}>{nval(r.resolutionRate, '%')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function TopicBars({ rows }: { rows: any[] }) {
  if (!rows?.length) return <div style={mutedLine}>Keine Themen erfasst.</div>;
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {rows.slice(0, 12).map((r) => (
        <div key={r.topic} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 130, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.topic}</span>
          <div style={{ flex: 1, background: 'var(--bg-elev-2)', borderRadius: 4, height: 14 }}>
            <div style={{ width: `${(r.n / max) * 100}%`, height: '100%', background: 'var(--tenant-accent, #7c3aed)', opacity: 0.85, borderRadius: 4 }} />
          </div>
          <span style={{ width: 28, fontSize: 11, textAlign: 'right', color: 'var(--muted, #8aa)' }}>{r.n}</span>
        </div>
      ))}
    </div>
  );
}
function Heatmap({ hm }: { hm: any }) {
  if (!hm?.grid) return <div style={mutedLine}>Keine Daten im Zeitraum.</div>;
  const grid: number[][] = hm.grid; const wd: string[] = hm.weekdays ?? ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const max = Math.max(1, ...grid.flat());
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse' }}>
        <thead><tr><th style={{ width: 28 }}></th>{Array.from({ length: 24 }, (_, h) => <th key={h} style={{ fontSize: 8.5, color: 'var(--muted, #8aa)', fontWeight: 500, padding: '0 1px' }}>{h}</th>)}</tr></thead>
        <tbody>
          {grid.map((row, r) => (
            <tr key={r}>
              <td style={{ fontSize: 10, color: 'var(--muted, #8aa)', paddingRight: 4 }}>{wd[r]}</td>
              {row.map((v, c) => (
                <td key={c} title={`${wd[r]} ${c}:00 — ${v} Calls`}
                  style={{ width: 14, height: 14, background: v ? `color-mix(in srgb, var(--tenant-accent, #7c3aed) ${Math.round((v / max) * 100)}%, transparent)` : 'var(--bg-elev-2)', borderRadius: 2 }} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const statInput: CSSProperties = { padding: '6px 8px', borderRadius: 7, border: '1px solid var(--hairline, #444)', background: 'var(--bg-elev-2)', color: 'inherit', fontSize: 12.5 };
const applyBtn: CSSProperties = { padding: '7px 16px', borderRadius: 7, border: 'none', background: 'var(--tenant-accent, #7c3aed)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const csvBtn: CSSProperties = { padding: '3px 10px', borderRadius: 6, border: '1px solid var(--hairline, #444)', background: 'transparent', color: 'var(--muted, #8aa)', fontSize: 11, fontWeight: 600, cursor: 'pointer' };
const tbl: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 };
const th: CSSProperties = { textAlign: 'left', padding: '4px 8px', color: 'var(--muted, #8aa)', fontWeight: 600, borderBottom: '1px solid var(--hairline, #232323)' };
const thR: CSSProperties = { ...th, textAlign: 'right' };
const td: CSSProperties = { padding: '4px 8px', borderBottom: '1px solid var(--hairline, #1c1c1c)' };
const tdR: CSSProperties = { ...td, textAlign: 'right' };

// ── Nutzer- & Rollenverwaltung ──────────────────────────────────────────────
interface ManagedUser { id: string; email: string; displayName: string; role: string; status: string; enabled: boolean; groups: { id: string; name: string }[] }
const ROLE_OPTS = [
  { v: 'AGENT', label: 'Berater' },
  { v: 'TENANT_ADMIN', label: 'Administrator' },
  { v: 'AUDITOR', label: 'Auditor' },
  { v: 'SUPERADMIN', label: 'Superadmin' },
];
const JSON_HEADERS = { 'Content-Type': 'application/json' };

// ── Branding: Logo + Hintergrund-Vorlagen ───────────────────────────────────
function BrandingPanel() {
  const [logo, setLogo] = useState<string | null>(localStorage.getItem('bamoa_tenant_logo'));
  const [bgs, setBgs] = useState<{ id: string; name: string; dataUrl: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [bgName, setBgName] = useState('');

  useEffect(() => {
    apiFetch('/api/tenant-admin/backgrounds').then((r) => (r.ok ? r.json() : [])).then((x) => Array.isArray(x) && setBgs(x)).catch(() => { /* */ });
  }, []);

  async function uploadLogo(file: File) {
    setBusy(true); setErr('');
    try {
      const dataUrl = await fileToLogoDataUrl(file);
      const r = await apiFetch('/api/tenant-admin/logo', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ dataUrl }) });
      if (r.ok) { setLogo(dataUrl); localStorage.setItem('bamoa_tenant_logo', dataUrl); }
      else { const b = await r.json().catch(() => ({})); setErr(b?.message || 'Upload fehlgeschlagen.'); }
    } catch (e: any) { setErr(e?.message || 'Bild konnte nicht verarbeitet werden.'); }
    finally { setBusy(false); }
  }
  async function removeLogo() {
    setBusy(true); setErr('');
    try {
      const r = await apiFetch('/api/tenant-admin/logo', { method: 'DELETE' });
      if (r.ok) { setLogo(null); localStorage.removeItem('bamoa_tenant_logo'); }
    } catch { /* */ } finally { setBusy(false); }
  }
  async function addBg(file: File) {
    setBusy(true); setErr('');
    try {
      const dataUrl = await fileToBackgroundDataUrl(file);
      const r = await apiFetch('/api/tenant-admin/backgrounds', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ name: bgName || file.name.replace(/\.[^.]+$/, ''), dataUrl }) });
      if (r.ok) { const bg = await r.json(); setBgs((xs) => [...xs, bg]); setBgName(''); }
      else { const b = await r.json().catch(() => ({})); setErr(b?.message || 'Upload fehlgeschlagen.'); }
    } catch (e: any) { setErr(e?.message || 'Bild konnte nicht verarbeitet werden.'); }
    finally { setBusy(false); }
  }
  async function delBg(id: string) {
    setBusy(true);
    try {
      const r = await apiFetch(`/api/tenant-admin/backgrounds/${id}`, { method: 'DELETE' });
      if (r.ok) setBgs((xs) => xs.filter((b) => b.id !== id));
    } catch { /* */ } finally { setBusy(false); }
  }

  return (
    <section style={card}>
      <div style={cardHead}>Branding</div>
      {err && <div style={{ color: '#d8785a', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}

      <div style={{ marginBottom: 18 }}>
        <div style={{ ...fieldLabel, marginBottom: 8 }}>Logo (Stele über dem Namen + Berater-Oberfläche)</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ width: 120, height: 60, borderRadius: 8, border: '1px solid var(--hairline, #232323)', background: '#0e0e10', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {logo ? <img src={logo} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: 11, color: 'var(--muted, #8aa)' }}>kein Logo</span>}
          </div>
          <label style={{ ...miniBtn, cursor: 'pointer' }}>
            {logo ? 'Logo ersetzen' : 'Logo hochladen'}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" style={{ display: 'none' }} disabled={busy}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadLogo(f); e.currentTarget.value = ''; }} />
          </label>
          {logo && <button style={{ ...miniBtn, color: '#DC2626', borderColor: 'rgba(239,68,68,0.4)' }} disabled={busy} onClick={removeLogo}>Entfernen</button>}
        </div>
        <div style={{ ...mutedLine, padding: '8px 0 0' }}>Ohne Logo bleibt alles wie bisher (Initial bzw. Mandantenname). Empfohlen: PNG mit transparentem Hintergrund. Wirkt nach erneutem Login der Mitarbeiter.</div>
      </div>

      <div>
        <div style={{ ...fieldLabel, marginBottom: 8 }}>Hintergrund-Vorlagen für alle Mitarbeiter</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          {bgs.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--muted, #8aa)' }}>Noch keine Vorlagen.</span>}
          {bgs.map((b) => (
            <div key={b.id} style={{ position: 'relative' }}>
              <div title={b.name} style={{ width: 96, height: 54, borderRadius: 8, backgroundImage: `url(${b.dataUrl})`, backgroundSize: 'cover', backgroundPosition: 'center', border: '1px solid var(--hairline, #232323)' }} />
              <button onClick={() => delBg(b.id)} disabled={busy} title="Löschen"
                style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: 10, border: 'none', background: '#DC2626', color: '#fff', fontSize: 12, cursor: 'pointer', lineHeight: '20px', padding: 0 }}>×</button>
              <div style={{ fontSize: 10.5, color: 'var(--muted, #8aa)', maxWidth: 96, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={bgName} onChange={(e) => setBgName(e.target.value)} placeholder="Name (optional)" style={{ ...input, width: 180 }} />
          <label style={{ ...primaryBtn, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
            Vorlage hinzufügen
            <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} disabled={busy}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void addBg(f); e.currentTarget.value = ''; }} />
          </label>
        </div>
      </div>
    </section>
  );
}


function UserManagementPanel() {
  const meId = typeof localStorage !== 'undefined' ? localStorage.getItem('bamoa_user_id') : null;
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('AGENT');
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ur, gr] = await Promise.all([
        apiFetch('/api/tenant-admin/users'),
        apiFetch('/api/groups'),
      ]);
      if (!ur.ok) throw new Error();
      setUsers(await ur.json());
      setGroups(gr.ok ? await gr.json() : []);
      setErr('');
    } catch { setErr('Nutzer konnten nicht geladen werden.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const toggleGroup = async (u: ManagedUser, groupId: string) => {
    const has = u.groups.some((g) => g.id === groupId);
    const nextIds = has ? u.groups.filter((g) => g.id !== groupId).map((g) => g.id) : [...u.groups.map((g) => g.id), groupId];
    setBusyId(u.id);
    try {
      const r = await apiFetch(`/api/tenant-admin/users/${u.id}/groups`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ groupIds: nextIds }) });
      if (r.ok) {
        const nextGroups = groups.filter((g) => nextIds.includes(g.id)).map((g) => ({ id: g.id, name: g.name }));
        setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, groups: nextGroups } : x)));
      } else { const b = await r.json().catch(() => ({})); setErr(b?.message || 'Gruppe konnte nicht geändert werden.'); }
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setBusyId(null); }
  };

  const changeRole = async (u: ManagedUser, newRole: string) => {
    if (newRole === u.role) return;
    setBusyId(u.id);
    try {
      const r = await apiFetch(`/api/tenant-admin/users/${u.id}/role`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ role: newRole }) });
      if (r.ok) setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, role: newRole } : x)));
      else { const b = await r.json().catch(() => ({})); setErr(b?.message || 'Rolle konnte nicht geändert werden.'); }
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setBusyId(null); }
  };

  const toggleEnabled = async (u: ManagedUser) => {
    setBusyId(u.id);
    try {
      const r = await apiFetch(`/api/tenant-admin/users/${u.id}/enabled`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ enabled: !u.enabled }) });
      if (r.ok) setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, enabled: !u.enabled } : x)));
      else { const b = await r.json().catch(() => ({})); setErr(b?.message || 'Status konnte nicht geändert werden.'); }
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setBusyId(null); }
  };

  const addUser = async () => {
    setAdding(true); setAddErr('');
    try {
      const r = await apiFetch('/api/tenant-admin/users', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ email, displayName: name, role }) });
      if (r.ok) { setEmail(''); setName(''); setRole('AGENT'); await load(); }
      else { const b = await r.json().catch(() => ({})); setAddErr(b?.message || 'Anlegen fehlgeschlagen.'); }
    } catch { setAddErr('Verbindung fehlgeschlagen.'); }
    finally { setAdding(false); }
  };

  const canAdd = EMAIL_OK(email) && name.trim().length >= 2;

  return (
    <section style={card}>
      <div style={cardHead}>Nutzer & Rollen</div>

      {/* Anlegen */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 8 }}>
        <label style={{ ...fieldCol, flex: 2, minWidth: 180 }}>
          <span style={fieldLabel}>E-Mail</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@firma.de" style={input} />
        </label>
        <label style={{ ...fieldCol, flex: 2, minWidth: 160 }}>
          <span style={fieldLabel}>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Vor- und Nachname" style={input} />
        </label>
        <label style={{ ...fieldCol, flex: 1, minWidth: 130 }}>
          <span style={fieldLabel}>Rolle</span>
          <select value={role} onChange={(e) => setRole(e.target.value)} style={input}>
            {ROLE_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </label>
        <button onClick={addUser} disabled={!canAdd || adding} style={{ ...primaryBtn, opacity: !canAdd || adding ? 0.5 : 1 }}>
          {adding ? 'Anlegen…' : 'Anlegen'}
        </button>
      </div>
      {addErr && <div style={{ color: '#d8785a', fontSize: 12.5, marginBottom: 10 }}>{addErr}</div>}
      <div style={{ fontSize: 11.5, color: 'var(--muted, #8aa)', marginBottom: 16 }}>
        Neue Konten melden sich mit dieser E-Mail und dem gemeinsamen Anmeldepasswort an.
      </div>

      {/* Liste */}
      {err && <div style={{ color: '#d8785a', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
      {loading ? <div style={mutedLine}>Wird geladen…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {users.map((u) => {
            const isSelf = u.id === meId;
            return (
              <div key={u.id} style={{ borderBottom: '1px solid var(--hairline-soft)', padding: '10px 2px', opacity: u.enabled ? 1 : 0.55 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_DOT[u.status] || STATUS_DOT.offline, flexShrink: 0 }} />
                      {u.displayName}{isSelf && <span style={{ fontSize: 11, color: 'var(--muted, #8aa)', fontWeight: 500 }}>(Sie)</span>}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--muted, #8aa)' }}>{u.email}</span>
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <select
                      value={u.role}
                      disabled={isSelf || busyId === u.id}
                      onChange={(e) => changeRole(u, e.target.value)}
                      style={{ ...input, padding: '6px 8px', fontSize: 12.5, opacity: isSelf ? 0.6 : 1 }}
                      title={isSelf ? 'Die eigene Rolle kann nicht geändert werden' : undefined}
                    >
                      {ROLE_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                    <button
                      onClick={() => toggleEnabled(u)}
                      disabled={isSelf || busyId === u.id}
                      title={isSelf ? 'Das eigene Konto kann nicht deaktiviert werden' : (u.enabled ? 'Deaktivieren' : 'Aktivieren')}
                      style={{ ...miniBtn, opacity: isSelf ? 0.4 : 1, color: u.enabled ? '#16A34A' : 'var(--muted, #8aa)', borderColor: u.enabled ? 'rgba(34,197,94,0.4)' : 'var(--hairline, #232323)' }}
                    >
                      {u.enabled ? 'Aktiv' : 'Inaktiv'}
                    </button>
                  </span>
                </div>
                {groups.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, paddingLeft: 16, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: 'var(--muted, #8aa)' }}>Gruppen:</span>
                    {groups.map((g) => {
                      const on = u.groups.some((x) => x.id === g.id);
                      return (
                        <button key={g.id} onClick={() => toggleGroup(u, g.id)} disabled={busyId === u.id} style={pill(on)}>
                          {g.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function EMAIL_OK(e: string) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim()); }

// ── Gruppen-Verwaltung ──────────────────────────────────────────────────────
interface ManagedGroup { id: string; name: string; enabled: boolean; routingStrategy: string; pickupEnabled: boolean; visibility: string; rotationTimeoutSec: number; memberCount?: number; steleCount?: number }
const ROUTING_OPTS = [
  { v: 'longest-idle', label: 'Längste Pause zuerst' },
  { v: 'round-robin', label: 'Reihum (Round-Robin)' },
  { v: 'ring-all', label: 'Alle gleichzeitig (Ring-All)' },
];
const VIS_OPTS = [
  { v: 'group', label: 'Gruppenintern' },
  { v: 'tenant', label: 'Mandantenweit' },
  { v: 'none', label: 'Versteckt' },
];
const routingLabel = (v: string) => ROUTING_OPTS.find((o) => o.v === v)?.label || v;

function GroupsPanel() {
  const [groups, setGroups] = useState<ManagedGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [edit, setEdit] = useState<Partial<ManagedGroup>>({});
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newRouting, setNewRouting] = useState('longest-idle');
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/tenant-admin/groups');
      if (!r.ok) throw new Error();
      setGroups(await r.json());
      setErr('');
    } catch { setErr('Gruppen konnten nicht geladen werden.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const createGroup = async () => {
    setCreating(true); setCreateErr('');
    try {
      const r = await apiFetch('/api/tenant-admin/groups', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ name: newName, routingStrategy: newRouting }) });
      if (r.ok) { const g = await r.json(); setGroups((xs) => [...xs, g].sort((a, b) => a.name.localeCompare(b.name))); setNewName(''); }
      else { const b = await r.json().catch(() => ({})); setCreateErr(b?.message || 'Anlegen fehlgeschlagen.'); }
    } catch { setCreateErr('Verbindung fehlgeschlagen.'); }
    finally { setCreating(false); }
  };

  const toggleEnabled = async (g: ManagedGroup) => {
    setBusyId(g.id);
    try {
      const r = await apiFetch(`/api/tenant-admin/groups/${g.id}`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ enabled: !g.enabled }) });
      if (r.ok) { const upd = await r.json(); setGroups((xs) => xs.map((x) => (x.id === g.id ? upd : x))); }
      else { const b = await r.json().catch(() => ({})); setErr(b?.message || 'Status konnte nicht geändert werden.'); }
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setBusyId(null); }
  };

  const openEdit = (g: ManagedGroup) => {
    if (expandedId === g.id) { setExpandedId(null); return; }
    setExpandedId(g.id); setConfirmDel(null);
    setEdit({ name: g.name, routingStrategy: g.routingStrategy, pickupEnabled: g.pickupEnabled, visibility: g.visibility, rotationTimeoutSec: g.rotationTimeoutSec });
  };

  const saveEdit = async (g: ManagedGroup) => {
    setBusyId(g.id);
    try {
      const r = await apiFetch(`/api/tenant-admin/groups/${g.id}`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(edit) });
      if (r.ok) { const upd = await r.json(); setGroups((xs) => xs.map((x) => (x.id === g.id ? upd : x)).sort((a, b) => a.name.localeCompare(b.name))); setExpandedId(null); }
      else { const b = await r.json().catch(() => ({})); setErr(b?.message || 'Speichern fehlgeschlagen.'); }
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setBusyId(null); }
  };

  const deleteGroup = async (g: ManagedGroup) => {
    setBusyId(g.id);
    try {
      const r = await apiFetch(`/api/tenant-admin/groups/${g.id}`, { method: 'DELETE' });
      if (r.ok) { setGroups((xs) => xs.filter((x) => x.id !== g.id)); setExpandedId(null); setConfirmDel(null); }
      else { const b = await r.json().catch(() => ({})); setErr(b?.message || 'Löschen fehlgeschlagen.'); }
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setBusyId(null); }
  };

  const canCreate = newName.trim().length >= 2;

  return (
    <section style={card}>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>Gruppen</div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 6 }}>
        <label style={{ ...fieldCol, flex: 2, minWidth: 160 }}>
          <span style={fieldLabel}>Name</span>
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="z. B. Baufinanzierung" style={input} />
        </label>
        <label style={{ ...fieldCol, flex: 2, minWidth: 180 }}>
          <span style={fieldLabel}>Routing-Strategie</span>
          <select value={newRouting} onChange={(e) => setNewRouting(e.target.value)} style={input}>
            {ROUTING_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </label>
        <button onClick={createGroup} disabled={!canCreate || creating} style={{ ...primaryBtn, opacity: !canCreate || creating ? 0.5 : 1 }}>
          {creating ? 'Anlegen…' : 'Anlegen'}
        </button>
      </div>
      {createErr && <div style={{ color: '#d8785a', fontSize: 12.5, marginBottom: 10 }}>{createErr}</div>}
      <div style={{ fontSize: 11.5, color: 'var(--muted, #8aa)', marginBottom: 16 }}>
        Gruppen bündeln Berater für die Anruf-Verteilung. Nutzer und Stelen ordnest du Gruppen unten zu.
      </div>

      {err && <div style={{ color: '#d8785a', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
      {loading ? <div style={mutedLine}>Wird geladen…</div> : groups.length === 0 ? <div style={mutedLine}>Keine Gruppen angelegt.</div> : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {groups.map((g) => (
            <div key={g.id} style={{ borderBottom: '1px solid var(--hairline-soft)', padding: '10px 2px', opacity: g.enabled ? 1 : 0.6 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                  <span style={{ fontWeight: 600 }}>{g.name}</span>
                  <span style={{ fontSize: 12, color: 'var(--muted, #8aa)' }}>
                    {routingLabel(g.routingStrategy)}
                    {g.memberCount !== undefined && ` · ${g.memberCount} Berater`}
                    {g.steleCount !== undefined && ` · ${g.steleCount} Stelen`}
                  </span>
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => toggleEnabled(g)} disabled={busyId === g.id} title={g.enabled ? 'Deaktivieren' : 'Aktivieren'}
                    style={{ ...miniBtn, color: g.enabled ? '#16A34A' : 'var(--muted, #8aa)', borderColor: g.enabled ? 'rgba(34,197,94,0.4)' : 'var(--hairline, #232323)' }}>
                    {g.enabled ? 'Aktiv' : 'Inaktiv'}
                  </button>
                  <button onClick={() => openEdit(g)} style={miniBtn}>{expandedId === g.id ? 'Schließen' : 'Bearbeiten'}</button>
                </span>
              </div>

              {expandedId === g.id && (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 10, background: 'var(--bg-elev-2)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <label style={{ ...fieldCol, flex: 2, minWidth: 150 }}>
                      <span style={fieldLabel}>Name</span>
                      <input value={edit.name ?? ''} onChange={(e) => setEdit((x) => ({ ...x, name: e.target.value }))} style={input} />
                    </label>
                    <label style={{ ...fieldCol, flex: 2, minWidth: 170 }}>
                      <span style={fieldLabel}>Routing-Strategie</span>
                      <select value={edit.routingStrategy ?? 'longest-idle'} onChange={(e) => setEdit((x) => ({ ...x, routingStrategy: e.target.value }))} style={input}>
                        {ROUTING_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                      </select>
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <label style={{ ...fieldCol, flex: 1, minWidth: 140 }}>
                      <span style={fieldLabel}>Sichtbarkeit</span>
                      <select value={edit.visibility ?? 'group'} onChange={(e) => setEdit((x) => ({ ...x, visibility: e.target.value }))} style={input}>
                        {VIS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                      </select>
                    </label>
                    <label style={{ ...fieldCol, flex: 1, minWidth: 120 }}>
                      <span style={fieldLabel}>Rotations-Timeout (s)</span>
                      <input type="number" min={5} max={600} value={edit.rotationTimeoutSec ?? 25} onChange={(e) => setEdit((x) => ({ ...x, rotationTimeoutSec: Number(e.target.value) }))} style={input} />
                    </label>
                    <button onClick={() => setEdit((x) => ({ ...x, pickupEnabled: !x.pickupEnabled }))}
                      style={{ ...miniBtn, height: 36, color: edit.pickupEnabled ? '#16A34A' : 'var(--muted, #8aa)', borderColor: edit.pickupEnabled ? 'rgba(34,197,94,0.4)' : 'var(--hairline, #232323)' }}>
                      Pickup: {edit.pickupEnabled ? 'An' : 'Aus'}
                    </button>
                    <button onClick={() => saveEdit(g)} disabled={busyId === g.id || (edit.name ?? '').trim().length < 2} style={{ ...primaryBtn, opacity: (edit.name ?? '').trim().length < 2 ? 0.5 : 1 }}>Speichern</button>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
                    {confirmDel === g.id ? (
                      <>
                        <span style={{ fontSize: 12, color: '#DC2626' }}>Gruppe löschen? Mitgliedschaften werden entfernt, Stelen verlieren die Zuordnung.</span>
                        <button onClick={() => deleteGroup(g)} disabled={busyId === g.id} style={{ ...miniBtn, color: '#fff', background: '#DC2626', border: 'none' }}>Endgültig löschen</button>
                        <button onClick={() => setConfirmDel(null)} style={miniBtn}>Abbrechen</button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmDel(g.id)} style={{ ...miniBtn, color: '#DC2626', borderColor: 'rgba(239,68,68,0.4)' }}>Löschen</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ── Stelen-Verwaltung ───────────────────────────────────────────────────────
type CameraRole = 'webcam' | 'doccam';
interface ManagedStele { id: string; name: string; location: string | null; groupId: string | null; groupName: string | null; status: string; enabled: boolean; lastSeenAt: string | null; url: string; masterTrackCameras?: CameraRole[] }

function fmtLastSeen(iso: string | null): string {
  if (!iso) return 'nie';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function StelesPanel() {
  const [steles, setSteles] = useState<ManagedStele[]>([]);
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editLoc, setEditLoc] = useState('');
  const [editMaster, setEditMaster] = useState<CameraRole[]>(['doccam']);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // Anlegen
  const [newName, setNewName] = useState('');
  const [newLoc, setNewLoc] = useState('');
  const [newGroup, setNewGroup] = useState('');
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, gr] = await Promise.all([apiFetch('/api/tenant-admin/steles'), apiFetch('/api/groups')]);
      if (!sr.ok) throw new Error();
      setSteles(await sr.json());
      setGroups(gr.ok ? await gr.json() : []);
      setErr('');
    } catch { setErr('Stelen konnten nicht geladen werden.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const createStele = async () => {
    setCreating(true); setCreateErr('');
    try {
      const r = await apiFetch('/api/tenant-admin/steles', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ name: newName, location: newLoc, groupId: newGroup || null }) });
      if (r.ok) { const s = await r.json(); setSteles((xs) => [...xs, s]); setNewName(''); setNewLoc(''); setNewGroup(''); }
      else { const b = await r.json().catch(() => ({})); setCreateErr(b?.message || 'Anlegen fehlgeschlagen.'); }
    } catch { setCreateErr('Verbindung fehlgeschlagen.'); }
    finally { setCreating(false); }
  };

  const toggleEnabled = async (s: ManagedStele) => {
    setBusyId(s.id);
    try {
      const r = await apiFetch(`/api/tenant-admin/steles/${s.id}/enabled`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ enabled: !s.enabled }) });
      if (r.ok) setSteles((xs) => xs.map((x) => (x.id === s.id ? { ...x, enabled: !s.enabled } : x)));
      else { const b = await r.json().catch(() => ({})); setErr(b?.message || 'Status konnte nicht geändert werden.'); }
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setBusyId(null); }
  };

  const changeGroup = async (s: ManagedStele, groupId: string) => {
    const gid = groupId || null;
    setBusyId(s.id);
    try {
      const r = await apiFetch(`/api/tenant-admin/steles/${s.id}/group`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ groupId: gid }) });
      if (r.ok) { const name = groups.find((g) => g.id === gid)?.name ?? null; setSteles((xs) => xs.map((x) => (x.id === s.id ? { ...x, groupId: gid, groupName: name } : x))); }
      else { const b = await r.json().catch(() => ({})); setErr(b?.message || 'Gruppe konnte nicht gesetzt werden.'); }
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setBusyId(null); }
  };

  const openEdit = (s: ManagedStele) => {
    if (expandedId === s.id) { setExpandedId(null); return; }
    setExpandedId(s.id); setEditName(s.name); setEditLoc(s.location || '');
    setEditMaster(Array.isArray(s.masterTrackCameras) ? s.masterTrackCameras : ['doccam']);
    setConfirmDel(null);
  };

  const toggleMaster = (role: CameraRole) => {
    setEditMaster((cur) => (cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role]));
  };

  const saveEdit = async (s: ManagedStele) => {
    setBusyId(s.id);
    try {
      const r = await apiFetch(`/api/tenant-admin/steles/${s.id}`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ name: editName, location: editLoc, masterTrackCameras: editMaster }) });
      if (r.ok) { const upd = await r.json(); setSteles((xs) => xs.map((x) => (x.id === s.id ? { ...x, ...upd } : x))); setExpandedId(null); }
      else { const b = await r.json().catch(() => ({})); setErr(b?.message || 'Speichern fehlgeschlagen.'); }
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setBusyId(null); }
  };

  const deleteStele = async (s: ManagedStele) => {
    setBusyId(s.id);
    try {
      const r = await apiFetch(`/api/tenant-admin/steles/${s.id}`, { method: 'DELETE' });
      if (r.ok) { setSteles((xs) => xs.filter((x) => x.id !== s.id)); setExpandedId(null); setConfirmDel(null); }
      else { const b = await r.json().catch(() => ({})); setErr(b?.message || 'Löschen fehlgeschlagen.'); }
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setBusyId(null); }
  };

  const copyLink = async (s: ManagedStele) => {
    try { await navigator.clipboard.writeText(s.url); setCopiedId(s.id); setTimeout(() => setCopiedId(null), 1500); } catch { /* */ }
  };

  const online = steles.filter((s) => s.status === 'online').length;
  const canCreate = newName.trim().length >= 2;

  return (
    <section style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>Stelen</span>
        <span style={{ fontSize: 12, color: 'var(--muted, #8aa)' }}>{online}/{steles.length} online</span>
      </div>

      {/* Anlegen */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 6 }}>
        <label style={{ ...fieldCol, flex: 2, minWidth: 150 }}>
          <span style={fieldLabel}>Name</span>
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="z. B. Stele Foyer" style={input} />
        </label>
        <label style={{ ...fieldCol, flex: 2, minWidth: 140 }}>
          <span style={fieldLabel}>Standort <span style={{ opacity: 0.5 }}>(optional)</span></span>
          <input value={newLoc} onChange={(e) => setNewLoc(e.target.value)} placeholder="z. B. Hauptbahnhof" style={input} />
        </label>
        <label style={{ ...fieldCol, flex: 1, minWidth: 130 }}>
          <span style={fieldLabel}>Gruppe</span>
          <select value={newGroup} onChange={(e) => setNewGroup(e.target.value)} style={input}>
            <option value="">— keine —</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </label>
        <button onClick={createStele} disabled={!canCreate || creating} style={{ ...primaryBtn, opacity: !canCreate || creating ? 0.5 : 1 }}>
          {creating ? 'Anlegen…' : 'Anlegen'}
        </button>
      </div>
      {createErr && <div style={{ color: '#d8785a', fontSize: 12.5, marginBottom: 10 }}>{createErr}</div>}
      <div style={{ fontSize: 11.5, color: 'var(--muted, #8aa)', marginBottom: 16 }}>
        Nach dem Anlegen findest du den Stele-Link über „Bearbeiten" – dort am Kiosk öffnen, um die Stele zu provisionieren.
      </div>

      {err && <div style={{ color: '#d8785a', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
      {loading ? <div style={mutedLine}>Wird geladen…</div> : steles.length === 0 ? <div style={mutedLine}>Keine Stelen angelegt.</div> : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {steles.map((s) => (
            <div key={s.id} style={{ borderBottom: '1px solid var(--hairline-soft)', padding: '10px 2px', opacity: s.enabled ? 1 : 0.6 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: s.status === 'online' ? '#22C55E' : '#9AA0A6' }} />
                    {s.name}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--muted, #8aa)' }}>{s.location || 'kein Standort'} · zuletzt {fmtLastSeen(s.lastSeenAt)}</span>
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <select value={s.groupId || ''} disabled={busyId === s.id} onChange={(e) => changeGroup(s, e.target.value)} style={{ ...input, width: 'auto', padding: '6px 8px', fontSize: 12.5 }} title="Gruppe zuweisen">
                    <option value="">— keine Gruppe —</option>
                    {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                  <button onClick={() => toggleEnabled(s)} disabled={busyId === s.id} title={s.enabled ? 'Deaktivieren' : 'Aktivieren'}
                    style={{ ...miniBtn, color: s.enabled ? '#16A34A' : 'var(--muted, #8aa)', borderColor: s.enabled ? 'rgba(34,197,94,0.4)' : 'var(--hairline, #232323)' }}>
                    {s.enabled ? 'Aktiv' : 'Inaktiv'}
                  </button>
                  <button onClick={() => openEdit(s)} style={miniBtn} title="Bearbeiten / Link">{expandedId === s.id ? 'Schließen' : 'Bearbeiten'}</button>
                </span>
              </div>

              {expandedId === s.id && (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 10, background: 'var(--bg-elev-2)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <label style={{ ...fieldCol, flex: 2, minWidth: 150 }}>
                      <span style={fieldLabel}>Name</span>
                      <input value={editName} onChange={(e) => setEditName(e.target.value)} style={input} />
                    </label>
                    <label style={{ ...fieldCol, flex: 2, minWidth: 150 }}>
                      <span style={fieldLabel}>Standort</span>
                      <input value={editLoc} onChange={(e) => setEditLoc(e.target.value)} style={input} />
                    </label>
                    <button onClick={() => saveEdit(s)} disabled={busyId === s.id || editName.trim().length < 2} style={{ ...primaryBtn, opacity: editName.trim().length < 2 ? 0.5 : 1 }}>Speichern</button>
                  </div>

                  <div style={{ ...fieldCol }}>
                    <span style={fieldLabel}>Master-Track-Kameras <span style={{ opacity: 0.6 }}>(hochauflösender Detail-Zoom)</span></span>
                    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', padding: '2px 0' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={editMaster.includes('doccam')} onChange={() => toggleMaster('doccam')} />
                        Dokumentenkamera
                      </label>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={editMaster.includes('webcam')} onChange={() => toggleMaster('webcam')} />
                        Webcam
                      </label>
                    </div>
                    <span style={{ ...mutedLine, padding: '2px 0 0', fontSize: 11.5 }}>
                      Master = Kamera wird in maximaler Auflösung geöffnet, der Zoom des Beraters bleibt detailerhaltend.
                      Empfohlen nur für die Dokumentenkamera; die Webcam als Master kostet zusätzlich Bandbreite und CPU.
                      Wirkt nach dem nächsten Stele-Neustart bzw. der nächsten Kamera-Initialisierung.
                    </span>
                  </div>

                  <div style={{ ...fieldCol }}>
                    <span style={fieldLabel}>Stele-Link <span style={{ opacity: 0.6 }}>(am Kiosk öffnen)</span></span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input value={s.url} readOnly onFocus={(e) => e.currentTarget.select()} style={{ ...input, fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }} />
                      <button onClick={() => copyLink(s)} style={miniBtn}>{copiedId === s.id ? '✓ Kopiert' : 'Kopieren'}</button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center', marginTop: 2 }}>
                    {confirmDel === s.id ? (
                      <>
                        <span style={{ fontSize: 12, color: '#DC2626' }}>Wirklich löschen? Auch die Call-Historie der Stele entfällt.</span>
                        <button onClick={() => deleteStele(s)} disabled={busyId === s.id} style={{ ...miniBtn, color: '#fff', background: '#DC2626', border: 'none' }}>Endgültig löschen</button>
                        <button onClick={() => setConfirmDel(null)} style={miniBtn}>Abbrechen</button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmDel(s.id)} style={{ ...miniBtn, color: '#DC2626', borderColor: 'rgba(239,68,68,0.4)' }}>Löschen</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ── Audit-Log ───────────────────────────────────────────────────────────────
interface AuditEntry {
  seq: string; eventType: string; actorId: string | null; actorName: string | null;
  subjectId: string | null; subjectName: string | null; detail: any; createdAt: string;
}
const EVENT_LABEL: Record<string, string> = {
  'login': 'Anmeldung', 'logout': 'Abmeldung', 'session-expired': 'Sitzung abgelaufen',
  'call.initiated': 'Anruf gestartet', 'call.accepted': 'Anruf angenommen', 'call.declined': 'Anruf abgelehnt',
  'call.ended': 'Anruf beendet', 'feedback': 'Feedback', 'agent-feedback': 'Berater-Feedback',
  'permission-change': 'Berechtigung geändert', 'cipher-weak': 'Schwache Verschlüsselung',
  'recording-started': 'Aufzeichnung gestartet', 'recording-finalized': 'Aufzeichnung abgeschlossen',
  'call-quality': 'Verbindungsqualität', 'document-signed': 'Dokument signiert',
};
const FILTERABLE = ['all', 'permission-change', 'login', 'logout', 'call.initiated', 'call.accepted', 'call.ended', 'recording-finalized', 'document-signed', 'cipher-weak'];

function fmtTs(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
function summarizeDetail(d: any): string {
  if (!d || typeof d !== 'object') return '';
  return Object.entries(d).slice(0, 4)
    .map(([k, v]) => `${k}: ${v && typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' · ');
}

function AuditPanel({ readOnly = false }: { readOnly?: boolean }) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [integrity, setIntegrity] = useState<{ ok: boolean; brokenAtSeq?: string } | null>(null);
  const [type, setType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [resealing, setResealing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async (t: string) => {
    setLoading(true); setErr('');
    try {
      const r = await apiFetch(`/api/tenant-admin/audit?type=${encodeURIComponent(t)}&limit=150`);
      if (!r.ok) throw new Error();
      const data = await r.json();
      setEntries(Array.isArray(data.entries) ? data.entries : []);
    } catch { setErr('Audit-Log konnte nicht geladen werden.'); }
    finally { setLoading(false); }
  }, []);

  const loadIntegrity = useCallback(async () => {
    try {
      const r = await apiFetch('/api/tenant-admin/audit/integrity');
      if (r.ok) setIntegrity(await r.json());
    } catch { /* optional */ }
  }, []);

  useEffect(() => { void load(type); }, [load, type]);
  useEffect(() => { void loadIntegrity(); }, [loadIntegrity]);

  const reseal = async () => {
    setResealing(true);
    try {
      const r = await apiFetch('/api/tenant-admin/audit/reseal', { method: 'POST' });
      if (r.ok) { await loadIntegrity(); await load(type); }
      else setErr('Neu-Versiegeln fehlgeschlagen.');
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setResealing(false); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      // Vollständiger Export (alle Einträge, nicht nur die angezeigten 150).
      const r = await apiFetch(`/api/tenant-admin/audit/export?type=${encodeURIComponent(type)}`);
      const data = r.ok ? await r.json() : { entries };
      const all: AuditEntry[] = Array.isArray(data.entries) ? data.entries : entries;
      const esc = (s: any) => `"${String(s ?? '').replace(/"/g, '""')}"`;
      const header = ['seq', 'zeitpunkt', 'ereignis', 'auslöser', 'betroffener', 'detail'];
      const rows = all.map((e) => [
        e.seq, new Date(e.createdAt).toISOString(), e.eventType,
        e.actorName || e.actorId || '', e.subjectName || e.subjectId || '',
        e.detail ? JSON.stringify(e.detail) : '',
      ].map(esc).join(','));
      const csv = '﻿' + [header.map(esc).join(','), ...rows].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { setErr('Export fehlgeschlagen.'); }
    finally { setExporting(false); }
  };

  return (
    <section style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>Audit-Log</span>
          {integrity && (
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.04em', padding: '3px 9px', borderRadius: 20,
              color: integrity.ok ? '#16A34A' : '#DC2626', background: integrity.ok ? 'rgba(34,197,94,0.14)' : 'rgba(239,68,68,0.14)' }}>
              {integrity.ok ? '✓ Kette intakt' : `⚠ Bruch bei #${integrity.brokenAtSeq}`}
            </span>
          )}
          {integrity && !integrity.ok && !readOnly && (
            <button onClick={reseal} disabled={resealing} style={{ ...miniBtn, color: 'var(--tenant-accent, #7c3aed)', borderColor: 'var(--tenant-accent, #7c3aed)' }}>
              {resealing ? 'Wird versiegelt…' : 'Kette neu versiegeln'}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={type} onChange={(e) => setType(e.target.value)} style={{ ...input, width: 'auto', padding: '6px 10px', fontSize: 12.5 }}>
            {FILTERABLE.map((t) => <option key={t} value={t}>{t === 'all' ? 'Alle Ereignisse' : (EVENT_LABEL[t] || t)}</option>)}
          </select>
          <button onClick={exportCsv} disabled={exporting} style={miniBtn} title="Vollständigen Export als CSV (alle Einträge)">{exporting ? 'Export…' : 'CSV'}</button>
          <button onClick={() => load(type)} style={miniBtn} title="Aktualisieren">Aktualisieren</button>
        </div>
      </div>

      {err && <div style={{ color: '#d8785a', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
      {loading ? <div style={mutedLine}>Wird geladen…</div> : entries.length === 0 ? <div style={mutedLine}>Keine Einträge.</div> : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {entries.map((e) => (
            <div key={e.seq} style={{ display: 'grid', gridTemplateColumns: '150px 180px 1fr', gap: 12, padding: '8px 2px', borderBottom: '1px solid var(--hairline-soft)', fontSize: 12.5, alignItems: 'baseline' }}>
              <span style={{ color: 'var(--muted, #8aa)', fontVariantNumeric: 'tabular-nums' }}>{fmtTs(e.createdAt)}</span>
              <span style={{ fontWeight: 600 }}>{EVENT_LABEL[e.eventType] || e.eventType}</span>
              <span style={{ color: 'var(--muted, #8aa)', wordBreak: 'break-word' }}>
                {e.actorName && <span style={{ color: 'var(--fg, #ececec)' }}>{e.actorName}</span>}
                {e.subjectName && <span> → {e.subjectName}</span>}
                {summarizeDetail(e.detail) && <span>{(e.actorName || e.subjectName) ? ' · ' : ''}{summarizeDetail(e.detail)}</span>}
              </span>
            </div>
          ))}
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--muted, #8aa)', marginTop: 12 }}>
        Neueste 150 Einträge. Die Hash-Kette belegt Unveränderlichkeit – eine lückenlose Sequenz bedeutet, dass nichts nachträglich geändert wurde.
      </div>
    </section>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────
const shell: CSSProperties = { position: 'fixed', inset: 0, overflow: 'auto', background: 'var(--bg, #0a0a0a)', color: 'var(--fg, #ececec)', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" };
const headerBar: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 22px', borderBottom: '1px solid var(--hairline, #232323)', position: 'sticky', top: 0, background: 'var(--bg, #0a0a0a)', zIndex: 5 };
const contentWrap: CSSProperties = { maxWidth: 1100, margin: '0 auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 20 };
const grid2: CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(340px, 1fr) minmax(340px, 1fr)', gap: 20, alignItems: 'start' };
const card: CSSProperties = { background: 'var(--bg-elev-1)', border: '1px solid var(--hairline, #232323)', borderRadius: 14, padding: 20 };
const cardHead: CSSProperties = { fontSize: 15, fontWeight: 700, marginBottom: 16, letterSpacing: '.01em' };
const settingTitle: CSSProperties = { fontSize: 13.5, fontWeight: 600, marginBottom: 4 };
const settingHint: CSSProperties = { fontSize: 12.5, color: 'var(--muted, #8aa)', lineHeight: 1.5, marginBottom: 10 };
const segRow: CSSProperties = { display: 'flex', gap: 6, background: 'var(--bg-elev-2)', padding: 4, borderRadius: 10 };
const mutedLine: CSSProperties = { fontSize: 13, color: 'var(--muted, #8aa)', padding: '6px 0 14px' };
const listRow: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 2px', borderBottom: '1px solid var(--hairline-soft)', fontSize: 13.5 };
const logoutBtn: CSSProperties = { background: 'transparent', border: '1px solid var(--hairline, #232323)', color: 'var(--muted, #8aa)', borderRadius: 8, padding: '7px 13px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
const deniedWrap: CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '80px 20px', textAlign: 'center' };
const fieldCol: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };
const fieldLabel: CSSProperties = { fontSize: 11.5, fontWeight: 600, color: 'var(--muted, #8aa)' };
const input: CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--hairline, #232323)', background: 'var(--bg-elev-2)', color: 'inherit', fontSize: 13.5, fontFamily: 'inherit', outline: 'none' };
const primaryBtn: CSSProperties = { border: 'none', background: 'var(--tenant-accent, #7c3aed)', color: '#fff', borderRadius: 8, padding: '9px 16px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };
const miniBtn: CSSProperties = { border: '1px solid var(--hairline, #232323)', background: 'transparent', borderRadius: 7, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };

function seg(active: boolean): CSSProperties {
  return { flex: 1, padding: '9px 12px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', background: active ? 'var(--tenant-accent, #7c3aed)' : 'transparent', color: active ? '#fff' : 'var(--muted, #8aa)' };
}
function pill(active: boolean): CSSProperties {
  return {
    padding: '3px 10px', borderRadius: 999, cursor: 'pointer', fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit',
    border: `1px solid ${active ? 'var(--tenant-accent, #7c3aed)' : 'var(--hairline, #232323)'}`,
    background: active ? 'var(--tenant-accent-soft)' : 'transparent',
    color: active ? 'var(--tenant-accent, #7c3aed)' : 'var(--muted, #8aa)',
  };
}
function radioRow(active: boolean): CSSProperties {
  return { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', background: active ? 'var(--tenant-accent-soft)' : 'var(--bg-elev-2)', border: `1px solid ${active ? 'var(--tenant-accent, #7c3aed)' : 'var(--hairline, #232323)'}`, color: 'inherit' };
}
function radioDot(active: boolean): CSSProperties {
  return { width: 16, height: 16, borderRadius: '50%', flexShrink: 0, border: `2px solid ${active ? 'var(--tenant-accent, #7c3aed)' : 'var(--muted, #8aa)'}`, background: active ? 'var(--tenant-accent, #7c3aed)' : 'transparent', boxShadow: active ? 'inset 0 0 0 2px var(--bg-elev-1)' : 'none' };
}

const ADMIN_CSS = `
@media (max-width: 820px) {
  .admin-grid2 { grid-template-columns: 1fr !important; }
}
`;

// ── Berater-Chat-Policy (Welle 11) ──────────────────────────────────────────
function ChatPolicyPanel() {
  const [p, setP] = useState<{ enabled: boolean; dmReach: 'shared-group' | 'all'; logToAudit: boolean; retentionDays: number }>(
    { enabled: true, dmReach: 'shared-group', logToAudit: false, retentionDays: 30 },
  );
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    apiFetch('/api/chat/policy', {}, { noLogoutOn401: true } as any)
      .then((r) => (r.ok ? r.json() : null))
      .then((x) => { if (x) setP({ enabled: !!x.enabled, dmReach: x.dmReach === 'all' ? 'all' : 'shared-group', logToAudit: !!x.logToAudit, retentionDays: Number(x.retentionDays) || 30 }); })
      .catch(() => setMsg('Aktuelle Einstellung konnte nicht geladen werden.'))
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await apiFetch('/api/chat/policy', { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(p) });
      if (r.ok) setMsg('Gespeichert.');
      else if (r.status === 403) setMsg('Keine Berechtigung für diese Änderung.');
      else setMsg('Speichern fehlgeschlagen.');
    } catch { setMsg('Verbindung fehlgeschlagen.'); }
    finally { setBusy(false); setTimeout(() => setMsg(''), 2500); }
  };

  const dis = busy || !loaded;
  const seg = (active: boolean): CSSProperties => ({
    flex: 1, textAlign: 'center', padding: '7px 10px', borderRadius: 8, cursor: dis ? 'default' : 'pointer',
    fontSize: 12.5, fontWeight: 600, background: active ? 'var(--tenant-accent, #7c3aed)' : 'transparent', color: active ? '#fff' : 'var(--muted, #8aa)',
  });

  return (
    <section style={card}>
      <div style={cardHead}>Berater-Chat</div>
      <div style={settingHint}>Interne Kollegenkommunikation – Direktnachrichten und Fragen an Gruppen, auch während eines Gesprächs.</div>

      <div style={settingTitle}>Chat aktiv</div>
      <div style={{ ...segRow, marginBottom: 16 }}>
        <div style={seg(p.enabled)} onClick={() => !dis && setP({ ...p, enabled: true })}>An</div>
        <div style={seg(!p.enabled)} onClick={() => !dis && setP({ ...p, enabled: false })}>Aus</div>
      </div>

      <div style={settingTitle}>Reichweite Direktnachrichten</div>
      <div style={settingHint}>Wer darf wem direkt schreiben?</div>
      <div style={{ ...segRow, marginBottom: 16 }}>
        <div style={seg(p.dmReach === 'shared-group')} onClick={() => !dis && setP({ ...p, dmReach: 'shared-group' })}>Gemeinsame Gruppe</div>
        <div style={seg(p.dmReach === 'all')} onClick={() => !dis && setP({ ...p, dmReach: 'all' })}>Alle Berater</div>
      </div>

      <div style={settingTitle}>Protokollierung</div>
      <div style={settingHint}>Nur Metadaten (Scope, Länge) im Audit-Log – kein Nachrichtentext.</div>
      <div style={{ ...segRow, marginBottom: 16 }}>
        <div style={seg(!p.logToAudit)} onClick={() => !dis && setP({ ...p, logToAudit: false })}>Intern (aus)</div>
        <div style={seg(p.logToAudit)} onClick={() => !dis && setP({ ...p, logToAudit: true })}>Protokollieren</div>
      </div>

      <div style={settingTitle}>Aufbewahrung (Tage)</div>
      <div style={settingHint}>Ältere Nachrichten werden automatisch gelöscht.</div>
      <input type="number" min={1} max={3650} value={p.retentionDays} disabled={dis}
        onChange={(e) => setP({ ...p, retentionDays: Math.max(1, Math.min(3650, Number(e.target.value) || 30)) })}
        style={{ ...input, maxWidth: 120, marginBottom: 18 }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={save} disabled={dis} style={primaryBtn}>Speichern</button>
        {msg && <span style={{ fontSize: 12.5, color: 'var(--muted, #8aa)' }}>{msg}</span>}
      </div>
    </section>
  );
}
